import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertOctagon, ChevronDown, Clock, ChevronsLeft, ChevronsRight, ChevronUp, LayoutGrid, Loader2, PlaneLanding, Truck,
  Wrench, X,
} from 'lucide-react'
import { freshCells, useFleet } from '../state/store'
import { sessionDisplayName, useSession } from '../auth/useSession'
import { useCan, useSiteAccess } from '../auth/useCan'
import { ago, clock, congestionTier, duration, isOnSiteCell, lastSeen, TIER_COLOR, vehicleState } from '../lib/format'
import { compareTimsFirst, overlayDriver, resolveTimsTrip, timsOnlyVehicle, tripEntryTs, uniqueTimsTrips } from '../lib/timsTrip'
import { locate } from '../lib/pointInPolygon'
import { rewriteStopMessage } from '../lib/stopPlace'
import { applyFilter, collapseSameLegAlerts, emptyFilter, type AlertFilter } from '../lib/recurring'
import {
  isAlertVisible, isSustainedAlertStillOpen, isSustainedConditionKind,
  SUSTAINED_ALERT_LIVENESS_MS, useSustainedAlertDeadSet,
} from '../lib/alertVisibility'
import { pollWhileVisible } from '../lib/pollWhileVisible'
import { onArrivalStrip } from '../lib/arrivalWatch'
import type { Alert, InboundRow, Vehicle } from '../lib/types'
import {
  CorridorPressure, defaultSurfaceFor, EmptyState, FilterChips, fleetPills, GeofenceCard, KpiCard, KpiStrip,
  MapLegendCard, MapModeSwitch, pathFor, SearchBox, SelectMenu, SidePanel, SortBar, surfacePills, TopBar,
  VehicleCard, type FleetId, type VehicleStatus,
} from '../cc'

import { fenceStyle } from '../map/layers/fenceIcons'
import { EVENT_CATEGORIES, eventCategoryOf } from '../map/layers/heatmap'
import { loadLabel } from '../map/layers/vehicles'
import { MapCanvas } from '../map/MapCanvas'
import { BasemapSwitch } from '../cc/BasemapSwitch'
import { AlertDetail } from '../panels/AlertDetail'
import { BreakdownForm } from '../panels/BreakdownForm'
import { BreakdownQuickResolve } from '../panels/BreakdownQuickResolve'
import {
  TripReplayBar, useTripReplayFrame, tripReplayAlerts, tripReplayCorrelationStart,
  TRIP_REPLAY_DEFAULT_SPEED, TRIP_REPLAY_TICK_MS, stepTripReplayPlayhead,
  type TripReplayPayload, type TripReplaySpeed, tripReplayWindow,
} from '../panels/TripReplay'
import { ReplayTripsPanel } from '../panels/ReplayTripsPanel'
import { TripTimelinePanel } from '../panels/TripTimeline'
import type { TripLogistics } from '../panels/tripLogistics'
import type { PlaybackCycle } from '../panels/RoundTripCycles'
import { VehicleDrawer } from '../panels/VehicleDrawer'
import { DetailWorkspace } from '../panels/DetailWorkspace'
import { pathToTrack, type TripPathPoint } from '../lib/tripPath'

type ViewMode = 'map' | 'detail'

const FULL_MAP_KEY = 'fleet.fullMap'

function readFullMap(): boolean {
  try { return sessionStorage.getItem(FULL_MAP_KEY) === '1' } catch { return false }
}

function writeFullMap(on: boolean): void {
  try { sessionStorage.setItem(FULL_MAP_KEY, on ? '1' : '0') } catch { /* */ }
}

// Distinct from fullMap: fullMap hides the fleet/alerts/actionables panel entirely so
// the map takes the whole view. This instead shrinks it to a slim rail -- more map
// room without losing the panel outright, since a dispatcher often wants both at once.
const PANEL_COLLAPSED_KEY = 'fleet.panelCollapsed'

function readPanelCollapsed(): boolean {
  try { return sessionStorage.getItem(PANEL_COLLAPSED_KEY) === '1' } catch { return false }
}

function writePanelCollapsed(on: boolean): void {
  try { sessionStorage.setItem(PANEL_COLLAPSED_KEY, on ? '1' : '0') } catch { /* */ }
}

/** Named Postgres windows — not free-form ranges. `live` = SSE tape only. */
type TimeWindow = 'today' | '1h' | 'since_yesterday' | 'week' | 'live'

/** loadHistory's own /api/alerts row cap -- named so it can't drift between the fetch
 *  URL and tripReplayAlertsCoverageUncertain's own "was this window's fetch truncated"
 *  check (round-3 adversarial review, HIGH follow-up). */
const HIST_ALERTS_FETCH_LIMIT = 200

const TIME_WINDOWS: { id: TimeWindow; label: string; hint: string }[] = [
  { id: 'today', label: 'Today', hint: 'Since midnight, updating live' },
  { id: '1h', label: 'Last 1 hour', hint: 'Rolling hour, updating live' },
  { id: 'since_yesterday', label: 'Since yesterday', hint: 'Yesterday 00:00 through now' },
  { id: 'week', label: 'Last week', hint: 'Rolling 7 days, midnight to now' },
  { id: 'live', label: 'Live stream', hint: 'Only what has come in this session — no history' },
]

/** Windowed ops KPIs from /api/ops/summary (Postgres). */
interface OpsSummary {
  window: string
  since: number
  total: number
  critical: number
  warn: number
  driver: number
  congestion: number
  vehicles: number
  trips: number
  trips_completed: number
  km: number
  hub_visits: number
  tat_breach_alerts: number
}

/**
 * The command centre: what is on the wall.
 *
 * Everything here answers a dispatcher's question in one glance. The KPI strip is the
 * shift in seven numbers, the left column is the exception list ordered worst-first, and
 * the map is where the corridor actually is. Nothing on this screen is decorative and
 * nothing needs a click to become legible -- it is read from across a room.
 *
 * Composed from the presentational layer in src/cc, which knows nothing about Kafka.
 * All the joining between live topics and those props happens here, in one place.
 */

const FLEET_FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'moving', label: 'MOVING' },
  { id: 'idle', label: 'IDLE' },
  { id: 'charging', label: 'CHARGING' },
  { id: 'flagged', label: 'FLAGGED' },
  { id: 'stale', label: 'OFFLINE' },
]

const SORTS = [
  { id: 'breakdown', label: 'Breakdown' },
  { id: 'speed', label: 'Speed' },
  { id: 'lastping', label: 'Latest' },
  { id: 'soc', label: 'SOC' },
]

const MAP_MODES = [
  { id: 'clusters', num: '1a', title: 'Clusters', sub: 'Spider out on zoom' },
  { id: 'congestion', num: '1b', title: 'Corridor pressure', sub: 'Where it is slow' },
  { id: 'events', num: '1c', title: 'Event density', sub: 'Where it goes wrong' },
]

/** Alert kinds that describe a driver rather than the network. */
const DRIVER_KINDS = new Set([
  'RASH_DRIVING', 'REPEAT_OVERSPEED', 'HARSH_BRAKE', 'HARSH_ACCEL', 'OVERSPEED',
  'GHAT_OVERSPEED', 'UNSCHEDULED_STOP', 'DIVERSION', 'CHECKPOINT_SKIPPED',
  'GHAT_DESCENT_RISK',
])

/**
 * Kinds that outrank their own severity in the queue.
 *
 * Severity alone is not enough to order this list. A WARN that says "this driver has been
 * over the limit twice in twenty minutes" needs a supervisor to pick up a phone right now;
 * a WARN that says "coolant is warm" does not, and sorting them together buries the first
 * under a dozen of the second. These are the kinds where the pattern IS the urgency, so
 * they float above everything at the same severity and sit alongside criticals.
 *
 * Deliberately short. A priority list that grows to a dozen entries has stopped
 * prioritising anything -- if everything floats, nothing does.
 */
export const PINNED_KINDS: Record<string, number> = {
  REPEAT_OVERSPEED: 2,   // deliberate, repeated, in a 55 T unit
  RASH_DRIVING: 1,
  LOW_SOC_AT_HUB: 1,     // the next dispatch cannot leave
}

/**
 * Fleet-card exception chip only. Everything else (STALE_FEED, ODO_GPS_DIVERGENCE,
 * OVERHEAT, DIVERSION, …) still lands on the Alerts tape / dashboard / map; it just
 * does not tint the vehicle card. GHAT_OVERSPEED is overspeed for this chip.
 */
export const CARD_EXCEPTION_KINDS = new Set([
  'LOW_SOC',
  'OVERSPEED',
  'GHAT_OVERSPEED',
  'HARSH_ACCEL',
  'HARSH_BRAKE',
  'UNSCHEDULED_STOP',
  'TAT_BREACH',
])

/**
 * Queue rank: severity first, then the pinned boost.
 *
 * The boost is added rather than substituted so a CRITICAL of an ordinary kind still
 * outranks a WARN of a pinned one -- a truck actually on fire beats a pattern of speeding.
 */
export function alertRank(a: Alert | undefined): number {
  if (!a) return 0
  const sev: Record<string, number> = { CRITICAL: 30, WARN: 20, INFO: 10 }
  return (sev[a.severity] ?? 0) + (PINNED_KINDS[a.kind] ?? 0)
}

const SEV_TONE: Record<string, 'danger' | 'warn' | 'info'> = {
  CRITICAL: 'danger', WARN: 'warn', INFO: 'info',
}

// Hoisted (adversarial review LOW, round 3): the ALERTS tab's empty state is rendered
// from two spots (the narrow-layout inline block and the wide-layout prop below) --
// one shared copy, not two that can quietly drift apart.
const ALERTS_EMPTY_TITLE_LIVE = 'Nothing in the last 5 min'
const ALERTS_EMPTY_TITLE_HISTORY = 'No alerts in window'
// Round 4 LOW: reworded -- the old copy said tiles age out purely on their own `ts`,
// which is no longer true for sustained-condition kinds (EXCESSIVE_IDLING/
// UNSCHEDULED_STOP), which stay open as long as the vehicle genuinely still is.
const ALERTS_EMPTY_BODY_LIVE = 'Nothing is currently firing or still open. '
  + 'Switch Time filter to Today for the full daily count.'
const ALERTS_EMPTY_BODY_HISTORY = 'Widen the time window to see more.'

/** What a KPI-card click should set the severity filter to -- reuses lib/recurring.ts's
 *  own `AlertFilter`/`emptyFilter` rather than inventing a second filter shape. 'all'
 *  clears it (the "Alerts today"/"Open alerts" tile); 'critical' narrows to CRITICAL
 *  only (the "Critical" tile). */
export function kpiAlertFilter(kind: 'all' | 'critical'): AlertFilter {
  if (kind === 'all') return emptyFilter()
  return { ...emptyFilter(), severities: new Set(['CRITICAL']) }
}

/** `/fleet?trip=<id>` -- how Reports > Alerts' trip drawer hands off to the full replay
 *  (reports/alerts/AlertTripDrawer.tsx's footer link). Returns the id to load plus the
 *  search params with it stripped, so the caller writes those back and neither a page
 *  refresh nor an unrelated later searchParams change re-fires the load -- the same
 *  consume-then-strip contract the `?vehicle=` effect below follows. Null when absent
 *  or blank. */
export function consumeTripParam(sp: URLSearchParams): { tripId: string; next: URLSearchParams } | null {
  const tripId = sp.get('trip')?.trim()
  if (!tripId) return null
  const next = new URLSearchParams(sp)
  next.delete('trip')
  return { tripId, next }
}

function isEmptyFilter(f: AlertFilter): boolean {
  return f.severities.size === 0 && f.kinds.size === 0 && f.subject === '' && f.sinceMs == null
}

/** Everything the trip-replay coverage decision needs, named rather than positional
 *  (round-3 adversarial-review follow-up, MED: two same-type `Alert[]` positional
 *  params -- the previous `(timeWindow, liveAlerts, histAndFreshAlerts)` shape --
 *  compile identically if swapped; a reviewer confirmed this is exactly how MED-5's
 *  triage-filter bug could silently come back). */
export interface TripReplayAlertsCoverageInputs {
  timeWindow: string
  /** `alerts` -- fleet-scoped, triage-UNFILTERED (round-3, MED-5: `tapeAlerts` drops
   *  triage-resolved alerts in live mode, which must not move this boundary). */
  liveAlerts: readonly Alert[]
  /** `tapeAlerts` in hist mode -- histAlerts + sseFresh merged, itself never
   *  triage-filtered in that branch, so it's already the right "what's in hand" source. */
  histAndFreshAlerts: readonly Alert[]
  /** `opsSummary?.since` -- the hist window's own requested start. Trustworthy as a
   *  coverage floor ONLY when `histTruncated` is false (see below); ignored otherwise. */
  requestedSince: number | null
  /** Whether the RAW (pre-fleet-filter) hist fetch hit `HIST_ALERTS_FETCH_LIMIT` --
   *  checked against the server's own row count, not `histAlerts.length`, because
   *  fleet-filtering can shrink 200 raw rows to well under 200 Ultratech ones, which
   *  would otherwise read as "untruncated" even though the DB query WAS capped.
   *  Untruncated means the ENTIRE window was actually fetched, so `requestedSince` is a
   *  real floor even when it's earlier than the earliest alert actually returned (that
   *  gap is confirmed clean, not unknown) -- truncated means only the data in hand can
   *  be trusted (round-3 adversarial-review follow-up, MED: `since` was discarded
   *  unconditionally by an earlier version of this fix, firing a false "incomplete"
   *  warning on the common, untruncated case). */
  histTruncated: boolean
  /** Whether a hist fetch has completed SUCCESSFULLY at least once for the current
   *  window -- `opsSummary != null`. False while loading, after a failed fetch, or when
   *  the viewer lacks the grant to fetch at all (round-3 adversarial-review follow-up,
   *  HIGH: an earlier version of this fix only forced "uncertain" on an empty LIVE tape;
   *  an empty, never-successfully-fetched HIST source read as "fully covered" just as
   *  wrongly, and hist mode is the default window). */
  histFetchSucceeded: boolean
}

function earliestTs(alerts: readonly Alert[]): number | null {
  return alerts.length ? Math.min(...alerts.map((a) => a.ts)) : null
}

/** The alert list a trip replay actually correlates against -- NOT `tapeAlerts`
 *  uniformly (round-3 follow-up #3, MED): `tapeAlerts` drops triage-resolved alerts in
 *  live mode, but a resolved alert still genuinely happened, and a post-hoc trip review
 *  must not hide it just because someone triaged it away since. Hist mode's
 *  `tapeAlerts` branch is never triage-filtered to begin with, so it stays the right
 *  source there -- and using it (not the raw, ALERT_CAP-bounded `liveAlerts`) is what
 *  keeps an old trip from showing zero alerts just because they scrolled out of the
 *  live tape hours ago. */
export function tripReplaySourceAlerts(
  timeWindow: string,
  liveAlerts: readonly Alert[],
  histAndFreshAlerts: readonly Alert[],
): readonly Alert[] {
  return timeWindow === 'live' ? liveAlerts : histAndFreshAlerts
}

/** Whether "zero correlated alerts" for this replay could be "zero we have data for"
 *  instead of "a clean trip". See `TripReplayAlertsCoverageInputs`'s own field docs for
 *  why each input matters -- this function's only job is to compose them correctly. */
export function tripReplayAlertsCoverageUncertain(
  replayCorrelationStart: number,
  inputs: TripReplayAlertsCoverageInputs,
): boolean {
  if (inputs.timeWindow === 'live') {
    const earliest = earliestTs(inputs.liveAlerts)
    if (earliest == null) return true
    return replayCorrelationStart < earliest
  }
  if (!inputs.histFetchSucceeded) return true
  const earliest = earliestTs(inputs.histAndFreshAlerts)
  if (inputs.histTruncated) {
    // A capped fetch proves nothing about anything before the earliest alert actually
    // in hand -- and if there is nothing in hand at ALL (round-3, second follow-up,
    // MED: the 200-row cap can be entirely consumed by other fleets' rows, leaving the
    // Ultratech-filtered slice empty even though the raw fetch WAS truncated), there is
    // nothing to anchor "covered" on either -- that is "unknown", not "confirmed clean".
    return earliest == null || replayCorrelationStart < earliest
  }
  const coverageStart = inputs.requestedSince != null
    ? (earliest != null ? Math.min(inputs.requestedSince, earliest) : inputs.requestedSince)
    : earliest
  // Fail CLOSED, not open: "untruncated, nothing in hand, no requested-since floor
  // known either" should not currently be reachable (OpsSummary.since is a required
  // field whenever the window actually matched), but if it ever is, treat it the same
  // as every other "we don't actually know" case above -- uncertain, not confirmed
  // clean (round-3 follow-up #3, LOW).
  return coverageStart == null || replayCorrelationStart < coverageStart
}

/** Builds the coverage inputs object at the one call site that needs it -- extracted
 *  (round-3 follow-up #4, MED) because the window-identity gating it does (comparing
 *  `opsSummary.window` to the currently selected `timeWindow`, round-3 follow-up #3's
 *  own F1 fix) lived only in the component body before this, where no test could ever
 *  exercise it: mutating `opsSummary != null` back in for the window check, or dropping
 *  the gate from any one of `requestedSince`/`histTruncated`/`histFetchSucceeded`
 *  without the other two, compiled and passed every existing test. `opsSummary`'s type
 *  here is the minimal structural shape this function actually reads, not the full
 *  (unexported) `OpsSummary` interface, so this stays callable from a test without
 *  constructing one. */
export function tripReplayAlertsCoverageInputsFor(
  timeWindow: string,
  opsSummary: { window: string; since: number } | null,
  histAlertsTruncated: boolean,
  liveAlerts: readonly Alert[],
  histAndFreshAlerts: readonly Alert[],
): TripReplayAlertsCoverageInputs {
  const opsSummaryMatchesWindow = opsSummary != null && opsSummary.window === timeWindow
  return {
    timeWindow,
    liveAlerts,
    histAndFreshAlerts,
    requestedSince: opsSummaryMatchesWindow ? opsSummary!.since : null,
    histTruncated: opsSummaryMatchesWindow ? histAlertsTruncated : true,
    histFetchSucceeded: opsSummaryMatchesWindow,
  }
}

/** Filter state transition on tab change: leaving the Alerts tab for any other tab
 *  always clears a severity filter back to empty, so a stale CRITICAL-only filter set by
 *  an earlier KPI-card click never silently narrows an unrelated later visit to the tab.
 *  Returns `current` unchanged (same reference) when it's already empty, rather than
 *  minting a fresh object on every tab switch for no observable difference. */
export function nextAlertSeverityFilter(tab: string, current: AlertFilter): AlertFilter {
  if (tab === 'alerts') return current
  return isEmptyFilter(current) ? current : emptyFilter()
}

export function CommandCentre(): JSX.Element {
  const navigate = useNavigate()
  const routeParams = useParams<{ tab?: string }>()
  const session = useSession()
  const userLabel = sessionDisplayName(session)
  // Alerts are raw signal, sometimes noisy; actionables are the triaged, act-on-this
  // subset. Everyone below admin should only ever see the latter.
  const isAdmin = session.status === 'signed-in' && session.session.is_admin !== false
  const isSuperAdmin = session.status === 'signed-in' && session.session.role === 'super_admin'
  // /api/ops/summary and /api/alerts (loadHistory below) are gated server-side by the
  // alerts grant + ultratech site -- call both hooks unconditionally here (Rules of
  // Hooks) and AND the results, rather than skip a hook call based on the other's value.
  const hasHistoryResourceGrant = useCan('alerts')
  const hasHistorySiteGrant = useSiteAccess('ultratech')
  const canLoadHistory = hasHistoryResourceGrant && hasHistorySiteGrant

  const vehicles = useFleet((s) => s.vehicles)
  // vehicles IS SHARED ACROSS EVERY NON-DIVERTED FLEET (Ultratech + Wonder Cement, as of
  // 2026-10, more later) -- this page is Ultratech-only and has no per-fleet UI, so every
  // full-roster enumeration below must filter to it explicitly, not assume the store
  // holds only Ultratech the way it always has until now. .get(id)/.has(id) lookups by a
  // SPECIFIC vehicle_id (from an already-Ultratech-scoped alert/breakdown) are left
  // unfiltered -- a Wonder Cement plate cannot collide with an Ultratech alert's
  // vehicle_id, so there is nothing those call sites need protecting from.
  const ultratechVehicles = useMemo(() => {
    const out = new Map<string, Vehicle>()
    for (const [id, v] of vehicles) {
      if (!v.fleet || v.fleet === 'transvolt-ultratech') out.set(id, v)
    }
    return out
  }, [vehicles])
  const rawAlerts = useFleet((s) => s.alerts)
  // Same gap and same fix as ultratechVehicles above: alerts is shared across every
  // non-diverted fleet (serving/app.py's handle() stamps `fleet` onto each alert from the
  // alerting vehicle's own record, specifically so consumers can do this). Filtered once
  // here, under the name every existing usage below already expects, rather than touching
  // each of the ~10 derived computations individually.
  const alerts = useMemo(
    () => rawAlerts.filter((a) => !a.fleet || a.fleet === 'transvolt-ultratech'),
    [rawAlerts],
  )
  const meta = useFleet((s) => s.meta)
  const timsTrips = useFleet((s) => s.timsTrips)
  const cells = useFleet((s) => s.cells)
  const progress = useFleet((s) => s.progress)
  const fenceIndex = useFleet((s) => s.fenceIndex)
  const geofences = useFleet((s) => s.geofences)
  const triage = useFleet((s) => s.triage)
  const breakdowns = useFleet((s) => s.breakdowns)
  const liveCounts = useFleet((s) => s.liveCounts)
  const rawActionables = useFleet((s) => s.actionables)
  // Same gap and same fix as alerts above -- serving/app.py's todays_actionable_criticals
  // and breakdowns dicts are shared across every non-diverted fleet, and until now carried
  // no fleet field at all to filter on. Confirmed live: a Wonder Cement breakdown/CRITICAL
  // rendered in Ultratech's own ACTIONABLES tab. `.get(vehicle_id)` lookups elsewhere in
  // this file against the raw `breakdowns` Map are left unfiltered, same reasoning as
  // ultratechVehicles' own comment -- they are always keyed by a vehicle_id that came from
  // an already-filtered alert/actionable item, so there is nothing to protect there.
  const actionables = useMemo(
    () => rawActionables
      .filter((a) => !a.fleet || a.fleet === 'transvolt-ultratech')
      // Drop anything already acknowledged/resolved from the active queue. `triage`
      // only ever holds non-'new' entries (store.ts deletes the map entry on 'new'),
      // so presence here means someone has already acted on it via AlertDetail.
      .filter((a) => !a.alert_id || !triage.has(a.alert_id)),
    [rawActionables, triage],
  )
  const conn = useFleet((s) => s.conn)
  const stats = useFleet((s) => s.stats)
  const selected = useFleet((s) => s.selectedVehicle)
  const openAlert = useFleet((s) => s.openAlert)
  const selectVehicle = useFleet((s) => s.selectVehicle)
  const selectFence = useFleet((s) => s.selectFence)
  const selectedFence = useFleet((s) => s.selectedFence)
  const flyTo = useFleet((s) => s.flyTo)
  const setTrack = useFleet((s) => s.setTrack)
  const openAlertAndReveal = useFleet((s) => s.openAlertAndReveal)
  const setFleetEfficiency = useFleet((s) => s.setFleetEfficiency)
  // How many trucks are arriving soon, for the tab badge -- same source AND same metric
  // as the "Arriving soon" section ArrivalBoard.tsx shows prominently at the top of the
  // page it opens, so the badge and the board never disagree.
  //
  // Confirmed live (2026-09-26): this used to hit /api/reports/tat -- the shared
  // kind-mixed dwell+transit view -- take the first 60 rows, and filter to
  // kind==='transit' client-side. Dwell events fire far more often than transit ones
  // (see that endpoint's own comment in serving/app.py), so the first 60 rows of the
  // mixed view were dominated by dwell events, leaving only a small, near-random number
  // of transit rows in the window -- observed stuck at 2/1, independent of how many
  // transit legs actually existed. Worse, "recent completed transit legs" was never the
  // right metric anyway: ArrivalBoard.tsx's own header comment is explicit that its
  // "Arrived" (completed-legs) count and its "Arriving soon" (ETA<=30m inbound) count
  // "were never meant to tally" -- and "Arriving soon" is the section a user actually
  // sees first when the badge takes them there. ARRIVING_SOON_MIN=30, duplicated from
  // ArrivalBoard.tsx's own constant rather than imported -- same reasoning that file's
  // own comments give for every other duplicated threshold in this codebase: a shared
  // import couples two independently-tunable UI surfaces.
  const ARRIVING_SOON_MIN = 30
  const [arrivalsCount, setArrivalsCount] = useState<number | undefined>(undefined)
  const setArrivalBoard = useFleet((s) => s.setArrivalBoard)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const r = await fetch('/api/reports/arrival_board')
        if (!r.ok) return
        const body = await r.json() as { rows?: InboundRow[]; error?: string }
        if (!r.ok || body.error || cancelled) return
        const rows = body.rows ?? []
        setArrivalsCount(
          rows.filter((row) => onArrivalStrip(row, ARRIVING_SOON_MIN)).length,
        )
        // Same rows feed MapCanvas's popup via the store -- see store.ts's own comment on
        // arrivalBoard -- so the map and this board never quote different distances.
        setArrivalBoard(rows)
      } catch { /* the tab simply shows no badge, and the popup keeps using live progress */ }
    }
    const stop = pollWhileVisible(() => void load(), 20_000)
    return () => { cancelled = true; stop() }
  }, [setArrivalBoard])
  const setHeatMode = useFleet((s) => s.setHeatMode)


  const revealVehicle = useCallback((id: string, fallback?: { lat: number; lon: number }) => {
    selectVehicle(id)
    const v = vehicles.get(id)
    // Live position wins when there is one; a broken-down vehicle is exactly the case
    // where there may not be, since the OBD/GPS feed can be the thing that went quiet --
    // fallback is where the breakdown module itself captured the vehicle at open.
    const pos = v?.lat != null && v?.lon != null ? { lat: v.lat, lon: v.lon } : fallback
    if (pos) flyTo({ lon: pos.lon, lat: pos.lat, zoom: 14 })
  }, [selectVehicle, vehicles, flyTo])

  // A WhatsApp breakdown alert's "Location" link (see _render_breakdown_alert_text in
  // serving/app.py): ?vehicle=<id> reveals that vehicle the same way clicking it in the
  // sidebar would. Waits for the vehicle to actually appear in the live SSE feed rather
  // than firing on mount -- on a cold page load `vehicles` is still empty, and revealVehicle
  // with no live position and no fallback is a no-op. Clears the param once fired so a
  // later manual deselect (or a page refresh with the link still in the address bar)
  // doesn't keep re-triggering the fly-to on every unrelated vehicles-map update.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const target = searchParams.get('vehicle')
    if (!target || !vehicles.has(target)) return
    revealVehicle(target)
    const next = new URLSearchParams(searchParams)
    next.delete('vehicle')
    setSearchParams(next, { replace: true })
  }, [searchParams, vehicles, revealVehicle, setSearchParams])

  // Only the transition between the map and a full detail page is routed -- /fleet vs.
  // /fleet/:tab. The compact sidebar's OWN tab (shown alongside the map) stays local
  // state: it is a small panel switching inside one page, not a distinct one, and routing
  // every click there would make Back annoyingly granular. mapTab is that local half;
  // `tab` below is whichever one is actually in effect for the current viewMode.
  const [mapTab, setMapTab] = useState('fleet')
  const viewMode: ViewMode = routeParams.tab ? 'detail' : 'map'
  const tab = routeParams.tab ?? mapTab
  const setTab = useCallback((next: string) => {
    if (routeParams.tab) navigate(`/fleet/${next}`)
    else setMapTab(next)
  }, [routeParams.tab, navigate])
  const setViewMode = useCallback((mode: ViewMode) => {
    if (mode === 'map') navigate('/fleet')
    else navigate(`/fleet/${tab}`)
  }, [navigate, tab])
  useEffect(() => {
    if (tab === 'alerts' && !isAdmin) setTab('actionables')
  }, [tab, isAdmin, setTab])

  // Severity drill-down for the Alerts tab, set by the KPI cards below (lib/recurring.ts's
  // own AlertFilter/applyFilter/emptyFilter -- no new filter abstraction). Leaving the
  // Alerts tab for any other tab always clears it, so a CRITICAL-only filter from an
  // earlier KPI click never silently narrows an unrelated later visit to the tab.
  const [alertSeverityFilter, setAlertSeverityFilter] = useState<AlertFilter>(emptyFilter())
  useEffect(() => {
    setAlertSeverityFilter((f) => nextAlertSeverityFilter(tab, f))
  }, [tab])

  // The breakdowns panel's "show on map" -- switches back to map view (a no-op if already
  // there, from the compact sidebar) so the vehicle's drawer (which already carries the
  // full breakdown status: reported at, resolve, photos) opens right where the truck is.
  const revealVehicleOnMap = useCallback((id: string, fallback?: { lat: number; lon: number }) => {
    setViewMode('map')
    revealVehicle(id, fallback)
  }, [revealVehicle])
  const [fullMap, setFullMap] = useState(readFullMap)
  const [panelCollapsed, setPanelCollapsed] = useState(readPanelCollapsed)

  /** In detail mode: browse tab grids, or focus one vehicle's telemetry cards. */
  const [detailFocus, setDetailFocus] = useState<'browse' | 'vehicle'>('browse')
  // Shared by every KPI-card drill-down below (adversarial-review fix, round 2, MED; and
  // round-3 follow-up #4, MED): a click used to be a no-op whenever the fleet panel was
  // collapsed (sessionStorage 'fleet.panelCollapsed' persists the collapse across the
  // whole tab session) -- the filter and tab both changed, but nothing on screen did,
  // because the alerts tab only renders inside the expanded SidePanel. A second, equally
  // silent no-op existed in detail mode with a vehicle focused: DetailWorkspace renders
  // VehicleDetailPage instead of any tab grid whenever `vehicleFocusId` is set, so the
  // tab/filter change was invisible there too, same failure class, different layout
  // branch (SidePanel's own "Breakdowns" header button already clears this the same way,
  // see setDetailFocus('browse') a few hundred lines below). Clearing it here means the
  // tile always lands somewhere visible, in map mode, detail-browse, or detail-vehicle-
  // focus alike.
  const openAlertsTab = useCallback((kind: 'all' | 'critical') => {
    setAlertSeverityFilter(kpiAlertFilter(kind))
    setPanelCollapsed(false)
    writePanelCollapsed(false)
    setDetailFocus('browse')
    setTab('alerts')
  }, [setTab])
  const [filter, setFilter] = useState('all')
  const [zoneType, setZoneType] = useState('all')
  const [showZoneTypes, setShowZoneTypes] = useState(false)
  const [sort, setSort] = useState('breakdown')
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState('clusters')
  // Default: today's daily counts from Postgres (live daily, not empty SSE tape).
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('today')
  const [histAlerts, setHistAlerts] = useState<Alert[]>([])
  // Whether the raw (PRE-fleet-filter) response hit HIST_ALERTS_FETCH_LIMIT -- checked
  // against the server's own row count, not histAlerts.length, because fleet-filtering
  // can shrink 200 raw rows (some Wonder Cement) down to well under 200 Ultratech ones,
  // which would otherwise read as "untruncated" even though the DB-level query WAS
  // capped (round-3 adversarial review follow-up).
  const [histAlertsTruncated, setHistAlertsTruncated] = useState(false)
  const [opsSummary, setOpsSummary] = useState<OpsSummary | null>(null)
  const [histError, setHistError] = useState<string | null>(null)
  // Distinguishes "still fetching" from "genuinely empty" -- without this the ALERTS tab's
  // first-load gap reads identically to "Queue clear," so a dispatcher sees nothing and
  // assumes the feed is broken rather than just slow.
  const [histLoading, setHistLoading] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const sustainedAlertDeadSet = useSustainedAlertDeadSet()
  // Real ingest rate, measured from consecutive stats samples rather than asserted.
  const [rate, setRate] = useState<number | null>(null)
  const lastStat = useRef<{ n: number; t: number } | null>(null)
  /** Vehicle id for the mark-breakdown form opened from Actionables / fleet. */
  const [breakdownFor, setBreakdownFor] = useState<string | null>(null)
  /** Geotag+halt trip replay (Postgres), separate from Kafka fleet scrubber. */
  const [tripReplay, setTripReplay] = useState<TripReplayPayload | null>(null)
  const [tripPath, setTripPath] = useState<TripPathPoint[] | null>(null)
  const [tripAt, setTripAt] = useState(0)
  const [tripPlaying, setTripPlaying] = useState(false)
  const [tripSpeed, setTripSpeed] = useState<TripReplaySpeed>(TRIP_REPLAY_DEFAULT_SPEED)
  const [tripFollow, setTripFollow] = useState(true)
  const [tripActiveId, setTripActiveId] = useState<string | null>(null)
  // Right-hand trip timeline during a replay; open by default, collapsible to a pill.
  const [timelineOpen, setTimelineOpen] = useState(true)
  // LR whose details the right column shows (clicked on the play bar); null = timeline.
  const [openLr, setOpenLr] = useState<string | null>(null)
  const replayKey = tripReplay ? `${tripReplay.kind}:${tripReplay.round_trip_id ?? tripReplay.trip_id ?? tripReplay.breakdown_id ?? ''}` : ''
  useEffect(() => { if (replayKey) setTimelineOpen(true) }, [replayKey])
  const tripFrame = useTripReplayFrame(tripReplay, tripAt, tripPath)

  const toggleFullMap = useCallback(() => {
    setFullMap((on) => {
      const next = !on
      writeFullMap(next)
      return next
    })
    setViewMode('map')
  }, [])

  useEffect(() => {
    if (!fullMap) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      writeFullMap(false)
      setFullMap(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullMap])

  const loadHistory = useCallback(async (window: Exclude<TimeWindow, 'live'>) => {
    setHistError(null)
    setHistLoading(true)
    try {
      const [sRes, aRes] = await Promise.all([
        fetch(`/api/ops/summary?window=${window}`),
        fetch(`/api/alerts?window=${window}&limit=${HIST_ALERTS_FETCH_LIMIT}`),
      ])
      const sBody = await sRes.json() as OpsSummary & { error?: string }
      const aBody = await aRes.json() as { alerts?: Alert[]; error?: string }
      if (!sRes.ok || sBody.error) throw new Error(sBody.error ?? `ops ${sRes.status}`)
      if (!aRes.ok || aBody.error) throw new Error(aBody.error ?? `alerts ${aRes.status}`)
      setOpsSummary(sBody)
      // Same filter `alerts`/`actionables` already apply to the live SSE slices above --
      // _query_alerts (serving/app.py) LEFT JOINs vehicle for `fleet` but does not filter
      // by it itself, so a Wonder Cement vehicle's alert arrives on this fetch with
      // fleet='transvolt-wondercement' and, left unfiltered, rendered in Ultratech's own
      // Alerts tab (confirmed live) for every 'today'/historical window -- 'live' was
      // never affected since it never reads histAlerts at all.
      const rawAlerts = aBody.alerts ?? []
      setHistAlertsTruncated(rawAlerts.length >= HIST_ALERTS_FETCH_LIMIT)
      setHistAlerts(rawAlerts.filter((a) => !a.fleet || a.fleet === 'transvolt-ultratech'))
    } catch (e: unknown) {
      setOpsSummary(null)
      setHistAlerts([])
      setHistAlertsTruncated(false)
      setHistError(e instanceof Error ? e.message : String(e))
    } finally {
      setHistLoading(false)
    }
  }, [])

  // The fleet's MEASURED efficiency, in place of the hardcoded prior. Fetched once and then
  // hourly: it is a distance-weighted average over 30 days of completed legs, so it moves
  // slowly, and polling it faster would be traffic for nothing. A failure is silent on
  // purpose -- the prior still works, and a console that refuses to draw a range because a
  // background refresh 404'd is worse than one quoting a slightly staler number.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [wholeLegRes, remainingRes] = await Promise.all([
          fetch('/api/reports/efficiency_by_load'),
          // LADEN-only, post-ghat descent -- see v_efficiency_remaining_by_load. Fetched
          // alongside the whole-leg figure so a truck past the split uses the segment
          // that actually describes what is left of its run, not the corridor average.
          fetch('/api/reports/efficiency_remaining_by_load'),
        ])
        if (!wholeLegRes.ok) return
        const body = await wholeLegRes.json() as { rows?: { vehicle_id: string; load_state: string; kwh_per_km: number }[] }
        const fleet = (body.rows ?? []).filter((x) => x.vehicle_id === '*')
        if (cancelled || !fleet.length) return
        let ladenRemaining: { kwhPerKm: number; splitLat: number } | null = null
        if (remainingRes.ok) {
          const rBody = await remainingRes.json() as {
            rows?: { load_state: string; kwh_per_km: number; split_lat: number }[]
          }
          const row = (rBody.rows ?? []).find((x) => x.load_state === 'LADEN')
          if (row && row.kwh_per_km > 0) {
            ladenRemaining = { kwhPerKm: row.kwh_per_km, splitLat: row.split_lat }
          }
        }
        setFleetEfficiency({
          laden: fleet.find((x) => x.load_state === 'LADEN')?.kwh_per_km ?? null,
          empty: fleet.find((x) => x.load_state === 'EMPTY')?.kwh_per_km ?? null,
          ladenRemaining,
        })
      } catch { /* the prior still works */ }
    }
    const stop = pollWhileVisible(() => void load(), 3_600_000)
    return () => { cancelled = true; stop() }
  }, [setFleetEfficiency])

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 2000)
    return () => clearInterval(id)
  }, [])

  // Advance geotag/halt trip replay in event time (default 8×; was ~300×).
  useEffect(() => {
    if (!tripPlaying || !tripReplay) return
    // ended_ts, else live_until (an open round trip plays to the moment it was loaded),
    // else the last event -- the same end the scrubber's own axis uses.
    const end = tripReplayWindow(tripReplay).end
    const id = window.setInterval(() => {
      setTripAt((prev) => {
        const stepped = stepTripReplayPlayhead(prev, end, tripSpeed)
        if (stepped.done) setTripPlaying(false)
        return stepped.at
      })
    }, TRIP_REPLAY_TICK_MS)
    return () => clearInterval(id)
  }, [tripPlaying, tripReplay, tripSpeed])

  const loadTripReplay = useCallback(async (tripId: string) => {
    const [replayRes, pathRes] = await Promise.all([
      fetch(`/api/trips/${encodeURIComponent(tripId)}/replay`),
      fetch(`/api/trips/${encodeURIComponent(tripId)}/path`),
    ])
    const body = await replayRes.json() as TripReplayPayload & { error?: string }
    if (!replayRes.ok || body.error) throw new Error(body.error ?? replayRes.statusText)
    const pathBody = await pathRes.json() as { points?: TripPathPoint[]; error?: string }
    setTripReplay(body)
    setTripPath(pathRes.ok && !pathBody.error ? (pathBody.points ?? []) : [])
    setTripActiveId(tripId)
    setTripAt(body.started_ts)
    setTripPlaying(false)
    setTripFollow(true)
    setMode('congestion')
    // Full-screen map mode forces liveOnly (see MapCanvas) and hides the scrubber bar
    // entirely -- a replay started from there would silently show the whole live fleet
    // instead of the one truck being replayed, with no visible way to watch it.
    writeFullMap(false)
    setFullMap(false)
  }, [])

  const loadRoundReplay = useCallback(async (roundId: string) => {
    const [replayRes, pathRes] = await Promise.all([
      fetch(`/api/round-trips/${encodeURIComponent(roundId)}/replay`),
      fetch(`/api/round-trips/${encodeURIComponent(roundId)}/path`),
    ])
    const body = await replayRes.json() as TripReplayPayload & { error?: string }
    if (!replayRes.ok || body.error) throw new Error(body.error ?? replayRes.statusText)
    const pathBody = await pathRes.json() as { points?: TripPathPoint[]; error?: string }
    setTripReplay(body)
    setTripPath(pathRes.ok && !pathBody.error ? (pathBody.points ?? []) : [])
    setTripActiveId(roundId)
    setTripAt(body.started_ts)
    setTripPlaying(false)
    setTripFollow(true)
    setMode('congestion')
    writeFullMap(false)
    setFullMap(false)
  }, [])

  // Dhar -> Dhule -> Dhar playback (serving/round_trip_playback.py): by cycle id, or --
  // for "Full round trip" on a leg -- the cycle that leg's vehicle was on at its start.
  const loadPlaybackCycle = useCallback(async (cycleId: string) => {
    const base = `/api/round-trips/playback/${encodeURIComponent(cycleId)}`
    const [replayRes, pathRes, lrRes] = await Promise.all([
      fetch(`${base}/replay`), fetch(`${base}/path`),
      // TIMS LR + driver for the play bar / LR panel; a failure just means no LR shown.
      fetch(`${base}/logistics`).catch(() => null),
    ])
    const body = await replayRes.json() as TripReplayPayload & { error?: string; detail?: string }
    if (!replayRes.ok || body.error) throw new Error(body.error ?? body.detail ?? replayRes.statusText)
    const pathBody = await pathRes.json() as { points?: TripPathPoint[]; error?: string }
    const logistics = lrRes && lrRes.ok ? await lrRes.json().catch(() => null) as TripLogistics | null : null
    setTripReplay({ ...body, logistics })
    setOpenLr(null)
    setTripPath(pathRes.ok && !pathBody.error ? (pathBody.points ?? []) : [])
    setTripActiveId(cycleId)
    setTripAt(body.started_ts)
    setTripPlaying(false)
    setTripFollow(true)
    setMode('congestion')
    writeFullMap(false)
    setFullMap(false)
  }, [])

  const loadRoundForLeg = useCallback(async (vehicleId: string, ts: number) => {
    const r = await fetch(`/api/round-trips/playback/containing?vehicle_id=${encodeURIComponent(vehicleId)}&ts=${ts}`)
    const body = await r.json() as Partial<PlaybackCycle> & { error?: string; detail?: string }
    if (!r.ok || !body.cycle_id) throw new Error(body.error ?? body.detail ?? 'no Dhar round trip covers this leg')
    await loadPlaybackCycle(body.cycle_id)
  }, [loadPlaybackCycle])

  const loadBreakdownReplay = useCallback(async (breakdownId: number) => {
    const [replayRes, pathRes] = await Promise.all([
      fetch(`/api/breakdown/${breakdownId}/replay`),
      fetch(`/api/breakdown/${breakdownId}/path`),
    ])
    const body = await replayRes.json() as TripReplayPayload & { error?: string }
    if (!replayRes.ok || body.error) throw new Error(body.error ?? replayRes.statusText)
    const pathBody = await pathRes.json() as { points?: TripPathPoint[]; error?: string }
    setTripReplay(body)
    setTripPath(pathRes.ok && !pathBody.error ? (pathBody.points ?? []) : [])
    setTripActiveId(`breakdown:${breakdownId}`)
    setTripAt(body.started_ts)
    setTripPlaying(false)
    setTripFollow(true)
    setMode('congestion')
    writeFullMap(false)
    setFullMap(false)
    setViewMode('map')
  }, [])

  const clearTripReplay = useCallback(() => {
    setTripReplay(null)
    setOpenLr(null)
    setTripPath(null)
    setTripActiveId(null)
    setTripPlaying(false)
    setTrack(null)
  }, [setTrack])

  // "Replay trip" for one vehicle: its round trip from Dhar -- the one it is on right now
  // (live, up to now) if any, else its most recent one in the last 14 days. Never a
  // single hub-to-hub leg.
  const replayVehicleTrip = useCallback(async (vehicleId: string) => {
    const vid = encodeURIComponent(vehicleId)
    const cur = await fetch(`/api/round-trips/playback/containing?vehicle_id=${vid}&ts=${Date.now()}`)
    let cycleId: string | undefined
    if (cur.ok) {
      cycleId = ((await cur.json()) as Partial<PlaybackCycle>).cycle_id
    } else {
      const r = await fetch(`/api/round-trips/playback?vehicle_id=${vid}&days=14`)
      const body = await r.json() as { cycles?: PlaybackCycle[]; error?: string }
      if (!r.ok || body.error) throw new Error(body.error ?? r.statusText)
      cycleId = body.cycles?.[0]?.cycle_id
    }
    if (!cycleId) throw new Error('No round trip from Dhar for this vehicle in the last 14 days')
    await loadPlaybackCycle(cycleId)
    setViewMode('map')
  }, [loadPlaybackCycle])

  // `?trip=<leg id>`: same consume-then-strip shape as the `?vehicle=` effect above, for
  // the alerts report's "Open in Command Centre replay" link. Replay is per round trip, so
  // the leg only tells us which truck and when: open the round trip from Dhar that covers
  // the leg's start, and fall back to the leg itself only if no round trip covers it.
  // Postgres-backed, so it does not wait on the live feed.
  useEffect(() => {
    const pending = consumeTripParam(searchParams)
    if (!pending) return
    setSearchParams(pending.next, { replace: true })
    void (async () => {
      const r = await fetch(`/api/trips/${encodeURIComponent(pending.tripId)}/replay`)
      const leg = await r.json() as Partial<TripReplayPayload> & { error?: string }
      if (!r.ok || !leg.vehicle_id || leg.started_ts == null) throw new Error(leg.error ?? 'trip not found')
      await loadRoundForLeg(leg.vehicle_id, leg.started_ts)
    })().catch(() => loadTripReplay(pending.tripId).catch(() => undefined))
  }, [searchParams, loadTripReplay, loadRoundForLeg, setSearchParams])

  const replaySelectedVehicleTrip = useCallback(() => {
    if (!selected) return
    void replayVehicleTrip(selected).catch(() => undefined)
  }, [selected, replayVehicleTrip])

  // Draw GPS trail up to scrubber time (teal track layer).
  useEffect(() => {
    if (!tripReplay || !tripPath?.length) return
    const track = pathToTrack(tripReplay.vehicle_id, tripPath, tripAt, { showEnds: false })
    setTrack(track)
  }, [tripReplay, tripPath, tripAt, setTrack])

  useEffect(() => {
    if (timeWindow === 'live') return
    // Department lacks the alerts grant (or the ultratech site) -- the server would 403
    // both requests. Skip the fetch entirely rather than surface that as histError: an
    // expected, by-design restriction should degrade the same way the already-silent
    // efficiency fetch does, not read as a scary failure banner.
    if (!canLoadHistory) return
    // Slow reconcile for trips/hubs (not on SSE). Alerts append live via SSE merge below.
    return pollWhileVisible(() => void loadHistory(timeWindow), 60_000)
  }, [timeWindow, loadHistory, canLoadHistory])

  useEffect(() => {
    setHeatMode(fullMap ? 'off' : mode === 'congestion' ? 'congestion' : mode === 'events' ? 'events' : 'off')
  }, [mode, fullMap, setHeatMode])

  useEffect(() => {
    if (!stats) return
    const prev = lastStat.current
    const nowT = Date.now()
    if (prev && nowT > prev.t + 3000) {
      setRate(Math.max(0, Math.round(((stats.consumed - prev.n) / (nowT - prev.t)) * 1000)))
    }
    if (!prev || nowT > prev.t + 3000) lastStat.current = { n: stats.consumed, t: nowT }
  }, [stats])

  // TV wall: hydrate from Postgres, then APPEND every SSE alert that falls in the window
  // so the left panel and KPI counts move without anyone hitting refresh.
  const histIds = useMemo(() => new Set(histAlerts.map((a) => a.alert_id)), [histAlerts])
  const windowSince = opsSummary?.since ?? 0

  const sseFresh = useMemo(() => {
    if (timeWindow === 'live' || !opsSummary) return [] as Alert[]
    return alerts.filter((a) => a.ts >= windowSince && !histIds.has(a.alert_id))
  }, [timeWindow, opsSummary, alerts, histIds, windowSince])

  // opsSummary.* is the deduped Postgres baseline (v_alert_deduped -- same-leg
  // OVERSPEED/GHAT_OVERSPEED collapsed). Adding raw sseFresh on top of that would count
  // a live repeat-overspeed burst once per firing instead of once per episode until the
  // next 60s loadHistory resync -- collapsing it the same way keeps the KPI cards from
  // inflating in between. Not perfect (a fresh ping continuing an episode opsSummary
  // already counted still adds one extra until the resync), but that's a small, transient
  // over-count, not the many-fold inflation an uncollapsed add produces.
  const sseFreshCollapsed = useMemo(() => collapseSameLegAlerts(sseFresh), [sseFresh])

  const tapeAlerts = useMemo(() => {
    const byId = timeWindow === 'live'
      ? new Map(alerts
          .filter((a) => triage.get(a.alert_id)?.state !== 'resolved')
          .map((a) => [a.alert_id, a]))
      : (() => {
          const m = new Map<string, Alert>()
          for (const a of histAlerts) m.set(a.alert_id, a)
          for (const a of sseFresh) m.set(a.alert_id, a)
          return m
        })()
    // Same rank-then-recency order everywhere -- 'live' used to skip this and show raw
    // arrival order, so a WARN that just landed sat above a CRITICAL from a minute ago.
    return [...byId.values()].sort((a, b) => alertRank(b) - alertRank(a) || b.ts - a.ts)
  }, [timeWindow, alerts, histAlerts, sseFresh, triage])

  // WHAT IS ON SCREEN RE-SORTS EVERY 8s, NOT ON EVERY SSE ALERT. tapeAlerts above is the
  // live truth (counts, badges, "N alerts" everywhere read it directly) -- but a busy
  // corridor can push several alerts a second, and re-deriving the sorted list on every
  // single one made rows visibly swap position continuously, which reads as the whole
  // panel flickering rather than as new information arriving. Re-sorting on a fixed
  // cadence instead gives an operator a stable few seconds to actually read and act on
  // what is on screen before anything moves -- new alerts still land within one tick,
  // nothing is delayed by more than that.
  const [displayAlerts, setDisplayAlerts] = useState<Alert[]>([])
  const tapeAlertsRef = useRef(tapeAlerts)
  tapeAlertsRef.current = tapeAlerts
  useEffect(() => {
    setDisplayAlerts(tapeAlertsRef.current)   // resync immediately on a window/mode change
    const id = setInterval(() => setDisplayAlerts(tapeAlertsRef.current), 8_000)
    return () => clearInterval(id)
  }, [timeWindow])

  // Cards only -- counts, badges, and the map exception chip all read tapeAlerts/displayAlerts
  // directly and stay un-collapsed. This just stops a burst of same-leg overspeed events from
  // rendering as a wall of near-identical cards.
  //
  // Live-tile TTL (.claude/work/SPEC.md): a card here must stop showing 5 minutes after its
  // own `ts` -- but ONLY on the 'live' window. 'today'/'7d'/etc are a deliberately chosen
  // historical range (Postgres-backed `histAlerts`/opsSummary), and an operator picking one
  // of those wants everything in it, not just the last 5 minutes. Reuses the existing `now`
  // tick (2s, above) as a new dependency -- no new timer.
  // Sustained-condition kinds (EXCESSIVE_IDLING/UNSCHEDULED_STOP) are exempted from
  // the ts TTL above -- see isSustainedAlertStillOpen's own comment for the staleness
  // gate/excluded-state gate/resurrection latch/max-age backstop this composes.
  // `moving === false` ALONE is not enough (adversarial review HIGH-1/HIGH-2):
  // vehicleState()'s 'charging' bucket (the same classification fleetRoster/
  // FLEET_FILTERS already trust elsewhere in this file) is reused for isExcludedState
  // -- the specific excluded state quixapp/rules/geofence.py's EXCESSIVE_IDLING rule
  // itself resets on -- but isLive is computed from a DEDICATED, much shorter bound
  // (SUSTAINED_ALERT_LIVENESS_MS, 5 minutes) rather than reusing vehicleState's own
  // 'stale' bucket, which is gated on STALE_AFTER_MS (45 minutes, calibrated for
  // map-pin desaturation, a different and far more forgiving concern) -- round 4 MED:
  // a truck silent for 44 minutes must not read as "confirmed still idling" here. "At
  // a hub" is the one excluded state this can't check cheaply (FenceIndex, the fast
  // lookup `fenceIndex` already is, does not carry hub_code -- only the full
  // GeofenceProps does) -- a documented, accepted gap, same category as MBMT's
  // missing depot signal.
  const groupedDisplayAlerts = useMemo(() => {
    const grouped = collapseSameLegAlerts(displayAlerts)
    if (timeWindow !== 'live') return grouped
    return grouped.filter((a) => {
      if (!a.vehicle_id || !isSustainedConditionKind(a.kind)) return isAlertVisible(a.ts, now)
      const v = ultratechVehicles.get(a.vehicle_id)
      const state = v ? vehicleState(v, now) : 'stale'
      return isSustainedAlertStillOpen({
        alertId: a.alert_id,
        ts: a.ts,
        now,
        isLive: v != null && now - lastSeen(v) <= SUSTAINED_ALERT_LIVENESS_MS,
        isMoving: state === 'moving',
        isExcludedState: state === 'charging',
      }, sustainedAlertDeadSet)
    })
  }, [displayAlerts, timeWindow, now, ultratechVehicles, sustainedAlertDeadSet])

  // The Alerts tab's own severity drill-down (KPI cards below set alertSeverityFilter).
  // Feeds BOTH the inline tab render and DetailWorkspace's `alerts` prop, so a filter set
  // from a KPI-card click applies identically whether the operator is looking at the
  // compact sidebar or the full-page detail layout.
  const filteredDisplayAlerts = useMemo(
    () => applyFilter(groupedDisplayAlerts, alertSeverityFilter),
    [groupedDisplayAlerts, alertSeverityFilter],
  )

  // Unresolved whitelist alert per vehicle becomes that vehicle's exception chip.
  // Worst severity wins over most recent: a critical from ten minutes ago outranks an
  // info from ten seconds ago, because that is the order a dispatcher works in.
  const worstByVehicle = useMemo(() => {
    const out = new Map<string, Alert>()
    for (const a of tapeAlerts) {
      if (!a.vehicle_id) continue
      if (!CARD_EXCEPTION_KINDS.has(a.kind)) continue
      if (triage.get(a.alert_id)?.state === 'resolved') continue
      const cur = out.get(a.vehicle_id)
      if (!cur || alertRank(a) > alertRank(cur)) out.set(a.vehicle_id, a)
    }
    return out
  }, [tapeAlerts, triage])

  // THE single roster every FLEET-tab list AND every FLEET-tab count must read from.
  // `rows` used to build this merge (live vehicles + TIMS-only orphans -- trips with no
  // live GPS match yet) by itself, so the rendered list included orphans but `k`'s bucket
  // tally and FilterChips' per-chip counts, which iterated `ultratechVehicles` directly,
  // never saw them. An orphan always resolves to 'stale' (timsOnlyVehicle sets
  // last_obd_ts/last_geo_ts to 0, which vehicleState reads as never-seen), so that split
  // was exactly "7 No-signal cards visible, No-signal count reads 0" and "moving+idle+
  // charging+stale doesn't add up to the total" -- two symptoms of one missing roster.
  const fleetRoster = useMemo(() => {
    const list = [...ultratechVehicles.values()].map((v) => {
      const st = vehicleState(v, now)
      const ex = worstByVehicle.get(v.vehicle_id)
      const where = v.lat != null && v.lon != null && fenceIndex.length
        ? locate(v.lon, v.lat, fenceIndex) : null
      const prog = progress.get(v.vehicle_id)
      const m = meta.get(v.vehicle_id)
      const trip = resolveTimsTrip(v.vehicle_id, timsTrips)
      const down = breakdowns.get(v.vehicle_id)
      return { v, st, ex, where, prog, m, trip, down, seen: lastSeen(v) }
    })
    const linked = new Set(list.map((r) => r.trip).filter(Boolean))
    for (const trip of uniqueTimsTrips(timsTrips)) {
      if (linked.has(trip)) continue
      const id = trip.vehicle_id ?? trip.tims_vehicle ?? 'unknown'
      const orphan = timsOnlyVehicle(id)
      list.push({
        v: orphan,
        st: vehicleState(orphan, now),
        ex: undefined,
        where: null,
        prog: undefined,
        m: undefined,
        trip,
        down: breakdowns.get(id),
        seen: tripEntryTs(trip) * 1000,
      })
    }
    return list
  }, [ultratechVehicles, worstByVehicle, fenceIndex, progress, meta, timsTrips, breakdowns, now])

  const rows = useMemo(() => {
    // Tokenized, not one literal substring -- "ramesh kumar" must match a driver stored
    // as "Ramesh Kumar" (or "Kumar Ramesh", or with irregular internal spacing) the same
    // way "ramesh" alone already did. A single `.includes(wholeQuery)` only ever matched
    // when the whole phrase appeared contiguous and byte-for-byte, which broke the instant
    // a user typed a first+last name a straight substring check couldn't satisfy.
    const qTokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const filtered = fleetRoster.filter((r) => {
      if (selectedFence && r.where?.id !== selectedFence) return false
      if (filter === 'flagged' ? !r.ex : filter !== 'all' && r.st !== filter) return false
      if (!qTokens.length) return true
      const driver = overlayDriver(r.trip, r.m) ?? ''
      const hay = `${r.v.vehicle_id} ${driver} ${r.where?.name ?? ''} `
        + `${r.m?.route_id ?? ''} ${r.ex?.message ?? ''} ${r.trip?.lr_number ?? ''} `
        + `${r.trip?.trailer ?? ''} ${r.trip?.event ?? ''}`
      const hayLower = hay.toLowerCase()
      return qTokens.every((t) => hayLower.includes(t))
    })
    filtered.sort((a, b) => compareTimsFirst(a, b, (r) => r.trip, (x, y) => {
      if (sort === 'speed') return (y.v.speed_kph ?? 0) - (x.v.speed_kph ?? 0)
      if (sort === 'lastping') {
        // No signal last regardless of how recently it was last seen -- a vehicle that
        // has gone quiet is not "latest" in any sense a dispatcher means by the word.
        if ((x.st === 'stale') !== (y.st === 'stale')) return x.st === 'stale' ? 1 : -1
        // Within everything still reporting, an active alert/breakdown floats to the top
        // ahead of plain recency -- the same priority 'breakdown' sort uses.
        const xFlag = Boolean(x.down) || Boolean(x.ex)
        const yFlag = Boolean(y.down) || Boolean(y.ex)
        if (xFlag !== yFlag) return xFlag ? -1 : 1
        if (xFlag) {
          const byFlag = (y.down ? 1 : 0) - (x.down ? 1 : 0) || alertRank(y.ex) - alertRank(x.ex)
          if (byFlag !== 0) return byFlag
        }
        // Most recently seen first -- "Latest" means latest, not oldest.
        return y.seen - x.seen
      }
      // Lowest SOC first -- the ops question this answers is "which truck needs charging
      // soonest," and an unknown reading (null) sorts last regardless, not first as 0 would.
      if (sort === 'soc') return (x.v.soc_pct ?? 101) - (y.v.soc_pct ?? 101)
      // 'breakdown': vehicles actually down come first, then worst exception, then recency.
      return (y.down ? 1 : 0) - (x.down ? 1 : 0) || alertRank(y.ex) - alertRank(x.ex) || x.seen - y.seen
    }))
    return filtered
  }, [fleetRoster, filter, query, sort, selectedFence])


  const zoneRows = useMemo(() => {
    const feats = geofences?.features ?? []
    // Same tokenized match as `rows` above -- see its own comment.
    const qTokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const occupancy = new Map<string, number>()
    for (const v of ultratechVehicles.values()) {
      if (v.lat == null || v.lon == null || !fenceIndex.length) continue
      const hit = locate(v.lon, v.lat, fenceIndex)
      if (!hit) continue
      occupancy.set(hit.id, (occupancy.get(hit.id) ?? 0) + 1)
    }
    const rows = feats.map((f) => {
      const p = f.properties
      const inside = occupancy.get(p.geofence_id) ?? 0
      return { f, p, inside, style: fenceStyle(p.type) }
    })
    const filtered = rows.filter((r) => {
      if (zoneType !== 'all' && String(r.p.type).toLowerCase() !== zoneType) return false
      if (!qTokens.length) return true
      const hay = `${r.p.name} ${r.p.type} ${r.p.hub_code ?? ''} ${r.p.geofence_id}`
      const hayLower = hay.toLowerCase()
      return qTokens.every((t) => hayLower.includes(t))
    })
    filtered.sort((a, b) =>
      b.inside - a.inside
      || a.style.label.localeCompare(b.style.label)
      || a.p.name.localeCompare(b.p.name))
    return filtered
  }, [geofences, vehicles, fenceIndex, zoneType, query])

  const zoneTypeChips = useMemo(() => {
    const feats = geofences?.features ?? []
    const counts = new Map<string, number>()
    for (const f of feats) {
      const t = String(f.properties.type ?? '').toLowerCase() || 'other'
      counts.set(t, (counts.get(t) ?? 0) + 1)
    }
    const types = [...counts.entries()]
      .sort((a, b) => fenceStyle(a[0]).label.localeCompare(fenceStyle(b[0]).label))
      .map(([id, count]) => ({ id, label: fenceStyle(id).label, count }))
    return [{ id: 'all', label: 'All', count: feats.length }, ...types]
  }, [geofences])

  const zoneSummary = useMemo(() => {
    const occupied = zoneRows.filter((r) => r.inside > 0).length
    const trucks = zoneRows.reduce((n, r) => n + r.inside, 0)
    return { occupied, trucks, shown: zoneRows.length }
  }, [zoneRows])

  const k = useMemo(() => {
    const list = [...ultratechVehicles.values()]
    // by/total read fleetRoster, not `list`: fleetRoster is the same live-vehicles-plus-
    // TIMS-orphans merge `rows` renders, so a TIMS trip with no GPS match yet (which always
    // reads as 'stale') is counted here the same way it is shown there. Reading `list`
    // instead is the bug this fixes -- see fleetRoster's own comment.
    const by = { moving: 0, idle: 0, charging: 0, stale: 0 }
    let inFence = 0
    for (const r of fleetRoster) {
      by[r.st] += 1
      if (r.where) inFence += 1
    }
    const fresh = [...cells.values()].filter((c) => (c._seen ?? 0) > now / 1000 - 900)
    const liveCongested = fresh.filter((c) => (c.score ?? 0) >= 0.55).length
    const liveOpen = alerts.filter((a) => triage.get(a.alert_id)?.state !== 'resolved')
    const speeds = list.map((v) => v.speed_kph ?? 0).filter((s) => s > 1)
    const hist = timeWindow !== 'live' && opsSummary
    // Baseline from Postgres + SSE arrivals not yet in the hydrate set (no refresh needed).
    const open = hist
      ? opsSummary.total + sseFreshCollapsed.length
      : liveOpen.length
    const critical = hist
      ? opsSummary.critical + sseFreshCollapsed.filter((a) => a.severity === 'CRITICAL').length
      : liveOpen.filter((a) => a.severity === 'CRITICAL').length
    const driverAlerts = hist
      ? opsSummary.driver + sseFreshCollapsed.filter((a) => DRIVER_KINDS.has(a.kind)).length
      : liveOpen.filter((a) => DRIVER_KINDS.has(a.kind)).length
    const congested = hist
      ? opsSummary.congestion
        + sseFreshCollapsed.filter((a) => a.kind === 'CONGESTION_START' || a.kind === 'CONGESTION_CLEARED').length
      : liveCongested
    // NOT lc.vehicles: confirmed live (2026-10-14) that _live_counts()'s `vehicles` is
    // `len(vehicles)` on the server -- every fleet, not Ultratech's own (serving/app.py has
    // no fleet filter on it, same gap breakdowns/actionables had before #106). With Wonder
    // Cement's 4-6 trucks folded in, this page's "Live vehicles" KPI read 73 while the
    // search counter and every bucket below it, correctly scoped to fleetRoster, totalled
    // 69 -- the exact "doesn't add up" complaint this file's own earlier fix (orphanCount,
    // comment above) only partly closed. fleetRoster.length is already Ultratech-scoped and
    // already includes TIMS orphans, so it is both the fix and the simplification: total is
    // always literally the roster's own size, nothing added, nothing borrowed from a number
    // computed over a different fleet. Accepts back the "local Map can hold a pruned ghost
    // until the next sync" imprecision lc.vehicles existed to paper over -- transient and
    // self-correcting (the server publishes `vehicles_removed` once it prunes one), unlike
    // the cross-fleet inflation it was trading that imprecision for, which is permanent and
    // only grows as more fleets share this deployment.
    const lc = liveCounts
    const total = fleetRoster.length
    return {
      total, ...by, inFence,
      // lc (live_counts) is the bounded TV-wall tape -- capped at 400 rows on hydration and
      // 600 in memory, nowhere near a busy day's real volume (1,225 alerts logged one day
      // this was checked against, vs 411 the tape had room for). Correct for 'live', where
      // "how many are on the live queue right now" IS the bounded tape's own job -- but
      // 'today' needs the real count, which open/critical/driverAlerts above already compute
      // correctly from opsSummary (Postgres, properly windowed) + sseFresh (the delta since).
      // congested already got this right; open/critical/driverAlerts previously did not.
      congested: lc && timeWindow === 'live' ? lc.congestion : congested,
      open: lc && timeWindow === 'live' ? lc.alerts : open,
      critical: lc && timeWindow === 'live' ? lc.critical : critical,
      driverAlerts: lc && timeWindow === 'live' ? lc.driver : driverAlerts,
      // NOT lc?.breakdowns/lc?.actionables: _live_counts() on the server computes both
      // across every fleet (len(breakdowns), len(todays_actionable_criticals) -- no fleet
      // filter, same gap `actionables` above was fixed for), so trusting it here would
      // show a bigger number than the now-correctly-filtered list underneath it, the same
      // mismatch in the opposite direction. Counted locally from the already-Ultratech-
      // filtered collections instead -- no ghost-vehicle lag concern to trade off against
      // (unlike `total` above, which has its own, different reason to prefer lc).
      breakdowns: [...breakdowns.values()].filter((b) => !b.fleet || b.fleet === 'transvolt-ultratech').length,
      actionables: actionables.length,
      trips: hist ? opsSummary.trips : null,
      km: hist ? opsSummary.km : null,
      avgSpeed: speeds.length ? Math.round(speeds.reduce((x, y) => x + y, 0) / speeds.length) : null,
      fences: geofences?.features.length ?? 0,
      cells: fresh.length,
      // Distinct devices that have reported, which is what a "gateway" count can honestly
      // mean here -- the pipeline has no gateway concept of its own to read.
      producers: new Set(list.map((v) => v.device_id).filter(Boolean)).size,
      histMode: Boolean(hist),
    }
  }, [ultratechVehicles, fleetRoster, cells, alerts, triage, fenceIndex, geofences, now,
      timeWindow, opsSummary, sseFresh, liveCounts, breakdowns, actionables])

  // The "Critical" KPI tile's own count (k.critical, live mode: serving/app.py's
  // _live_counts(), hist mode: _query_ops_summary()) is NOT fleet-scoped or
  // triage-resolved-filtered the way the list below is (alerts/groupedDisplayAlerts both
  // apply both) -- adversarial review (round 2) caught a specific "Showing N of M
  // critical -- list is capped" wording as actively WRONG in the common case: a gap
  // between the two numbers is usually a different fleet or an already-resolved alert in
  // k.critical, not a capped list. Deliberately NOT a specific count comparison -- say
  // what's actually true (the two numbers can come from different populations) rather
  // than a precise-sounding claim that is often the wrong explanation.
  const alertsFilterCaveat = alertSeverityFilter.severities.size > 0
    ? "This list is Ultratech-only and excludes resolved alerts; the KPI tile's own count may not match."
    : null

  // Congestion legend, one row per tier actually drawn on the map (t0 isn't -- see
  // heatmap.ts's DEGRADED filter) -- same freshCells() source MapCanvas draws from, so the
  // legend's counts never drift from what is actually on screen.
  const congestionTierCounts = useMemo(() => {
    const counts: Record<'t1' | 't2' | 't3', number> = { t1: 0, t2: 0, t3: 0 }
    for (const c of freshCells(cells, now)) {
      // Same on-site exclusion heatmap.ts's own DEGRADED filter applies -- otherwise
      // this legend counts Dhar's plant/yard dwell as "Severe" while the map itself (the
      // thing this legend is supposed to describe) draws nothing there.
      if (isOnSiteCell(c.fence_types)) continue
      const tier = congestionTier(c.score)
      if (tier !== 't0') counts[tier] += 1
    }
    return counts
  }, [cells, now])

  // Events legend, one row per colour category (heatmap.ts's EVENT_CATEGORIES) -- counted
  // from the same positioned-alert set eventFeatures() turns into heat points.
  const eventCategoryCounts = useMemo(() => {
    const counts = new Map<string, number>(EVENT_CATEGORIES.map((c) => [c.id, 0]))
    for (const a of alerts) {
      if (a.lat == null || a.lon == null) continue
      const cat = eventCategoryOf(a.kind)
      if (cat) counts.set(cat, (counts.get(cat) ?? 0) + 1)
    }
    return counts
  }, [alerts])

  // One legend per map mode -- a shared list mixing vehicle state, congestion and event
  // rows together answered no single question well. Clusters mode answers "what is my
  // fleet doing"; Corridor pressure answers "where and how bad"; Event density answers
  // "what kind of thing keeps happening here" -- three different questions, three lists.
  const legendRows = useMemo(() => {
    if (mode === 'congestion') {
      return [
        { icon: 'dot' as const, color: TIER_COLOR.t1, label: 'Slow', count: congestionTierCounts.t1 },
        { icon: 'dot' as const, color: TIER_COLOR.t2, label: 'Heavy', count: congestionTierCounts.t2 },
        { icon: 'dot' as const, color: TIER_COLOR.t3, label: 'Severe', count: congestionTierCounts.t3 },
      ]
    }
    if (mode === 'events') {
      return EVENT_CATEGORIES.map((c) => ({
        icon: 'dot' as const, color: c.color, label: c.label, count: eventCategoryCounts.get(c.id) ?? 0,
      }))
    }
    return [
      { icon: 'truck' as const, color: '#16A34A', label: 'Moving', count: k.moving },
      { icon: 'truck' as const, color: '#D97706', label: 'Idle', count: k.idle },
      { icon: 'truck' as const, color: '#2563EB', label: 'Charging', count: k.charging },
      { icon: 'truck' as const, color: '#94A3B8', label: 'Offline', count: k.stale },
    ]
  }, [mode, k, congestionTierCounts, eventCategoryCounts])

  // Corridor pressure: the worst congestion score in each slice of the route, so a
  // dispatcher sees where the corridor is bunching without reading a single cell id.
  const pressure = useMemo(() => {
    // On-site cells (plant/depot/yard/gate/queue/...) are scored by design -- trucks
    // queueing or parked at Dhar read as near-stopped the same way a real jam would --
    // but the backend deliberately never alerts on them (congestion.py's own ON_SITE_
    // TYPES). This bar answers "is the CORRIDOR moving," not "are trucks dwelling at the
    // plant," so on-site cells must not be allowed to paint it red -- without this,
    // Dhar's own gate/yard dwell permanently pins the leftmost bar regardless of how
    // clear the actual road is.
    const fresh = [...cells.values()]
      .filter((c) => (c._seen ?? 0) > now / 1000 - 900)
      .filter((c) => !isOnSiteCell(c.fence_types))
    const SLICES = 14
    const lat0 = 21.05, lat1 = 22.35
    const bars = Array.from({ length: SLICES }, () => 0)
    for (const c of fresh) {
      const t = (c.centroid_lat - lat0) / (lat1 - lat0)
      if (t < 0 || t > 1) continue
      const i = Math.min(SLICES - 1, Math.floor((1 - t) * SLICES))   // Dhar left, Dhule right
      bars[i] = Math.max(bars[i] ?? 0, c.score ?? 0)
    }
    const worst = Math.max(0, ...bars)
    return {
      bars: bars.map((value) => ({
        value,
        tone: (value >= 0.8 ? 'danger' : value >= 0.55 ? 'warn' : 'ok') as 'ok' | 'warn' | 'danger',
      })),
      verdict: worst >= 0.8 ? 'CONGESTED' : worst >= 0.55 ? 'SLOWING' : 'BALANCED',
      verdictTone: (worst >= 0.8 ? 'danger' : worst >= 0.55 ? 'warn' : 'ok') as 'ok' | 'warn' | 'danger',
    }
  }, [cells, now])

  const histFallback = useMemo(() => {
    if (!openAlert || timeWindow === 'live') return null
    return tapeAlerts.find((a) => a.alert_id === openAlert) ?? null
  }, [openAlert, timeWindow, tapeAlerts])

  const tripReplaySource = tripReplaySourceAlerts(timeWindow, alerts, tapeAlerts)

  // This one replay's own alerts, for the map's discrete marker layer -- same
  // correlation TripReplayBar does internally for its timeline dots, computed once
  // here so the map and the timeline agree on exactly what's plotted.
  const tripReplayAlertMarkers = useMemo(
    () => (tripReplay ? tripReplayAlerts(tripReplay, tripReplaySource) : null),
    [tripReplay, tripReplaySource],
  )

  // In hist mode, tapeAlerts is still bounded by whichever time window is CURRENTLY
  // selected (histAlerts = /api/alerts?window=${timeWindow}), which has no relationship
  // to the replay's own started_ts -- a trip from yesterday replayed while the page
  // reads "Today" finds zero alerts because none of yesterday's were ever fetched, not
  // because none fired. Flag that case explicitly rather than let it render identically
  // to a genuinely clean trip. See TripReplayAlertsCoverageInputs's own field docs for
  // what each input below means and why it can't be substituted for a nearby-looking
  // one (alerts vs. tapeAlerts, histAlertsTruncated's own raw-count requirement).
  //
  // `opsSummary?.window === timeWindow` (not a bare `opsSummary != null`, round-3
  // follow-up #3, MED): a reviewer found an earlier version of this fix NULLED
  // opsSummary on every window switch to avoid using a stale prior-window floor, which
  // broke the ~10 OTHER things on this page that read opsSummary for the CURRENT
  // window's real KPI figures (this file's own :1022-1028 comment on exactly why that
  // bounded-tape fallback must not show for a non-live window). Comparing the summary's
  // own `window` field to the one currently selected gets the same staleness guard
  // (including the narrower race where an in-flight fetch for a window the operator has
  // already switched away from resolves late) without touching that shared state at all.
  const tripReplayAlertsUncertain = Boolean(tripReplay) && tripReplayAlertsCoverageUncertain(
    tripReplay ? tripReplayCorrelationStart(tripReplay) : 0,
    tripReplayAlertsCoverageInputsFor(timeWindow, opsSummary, histAlertsTruncated, alerts, tapeAlerts),
  )

  const tripReplayBar = tripReplay ? (
    <TripReplayBar
      payload={tripReplay}
      at={tripAt}
      playing={tripPlaying}
      speed={tripSpeed}
      follow={tripFollow}
      onSeek={(ts) => { setTripAt(ts); setTripPlaying(false) }}
      onToggle={() => setTripPlaying((p) => !p)}
      onClear={clearTripReplay}
      onSpeed={setTripSpeed}
      onFollow={() => setTripFollow(true)}
      onPlayRound={tripReplay.kind === 'trip'
        ? () => {
          // The leg's own Dhar -> Dhule -> Dhar round trip, by the playback rule; fall back
          // to the stream's round trip only if the playback rule finds none.
          void loadRoundForLeg(tripReplay.vehicle_id, tripReplay.started_ts).catch(() => {
            if (tripReplay.round_trip_id) void loadRoundReplay(tripReplay.round_trip_id).catch(() => undefined)
          })
        }
        : undefined}
      alerts={tripReplaySource}
      alertsCoverageUncertain={tripReplayAlertsUncertain}
      openLr={openLr}
      onOpenLr={(lr) => { setOpenLr((cur) => (cur === lr ? null : lr)); setTimelineOpen(true) }}
    />
  ) : undefined

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#E9EEF5]">
      <div className="shrink-0">
      <TopBar
        clock={new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
        live={conn === 'live'}
        rate={rate != null ? `${rate}/s` : undefined}
        gateways={k.producers}
        user={userLabel}
        fleets={fleetPills(isSuperAdmin)}
        activeFleet="ultratech"
        onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
        nav={surfacePills('ultratech')}
        activeNav="command"
        onNav={(id) => navigate(pathFor('ultratech', id === 'reports' ? 'reports' : 'command'))}
        onSettings={() => navigate('/settings')}
        fullMap={fullMap}
        onFullMap={toggleFullMap}
      />
      </div>

      {!fullMap && (
      <div className="shrink-0 space-y-2 px-3 pt-3">
        <div className="flex items-center justify-end gap-3">
          {histError ? (
            <p className="min-w-0 flex-1 text-[11px] font-medium text-[#B45309]">{histError}</p>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <button
                type="button"
                onClick={() => { setDetailFocus('browse'); navigate('/fleet/breakdowns') }}
                className="flex h-8 items-center gap-1.5 rounded-[8px] border border-[rgba(15,23,42,0.08)]
                            bg-white px-2.5 text-[12px] font-medium text-[#475569] transition-colors
                            hover:border-[rgba(15,23,42,0.16)] hover:text-[#0F172A]
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
              >
                <AlertOctagon size={13} />
                Breakdowns
                {k.breakdowns ? (
                  <span className="font-mono text-[11px] tabular-nums text-[#94A3B8]">{k.breakdowns}</span>
                ) : null}
              </button>
              <button
                type="button"
                onClick={() => { setDetailFocus('browse'); navigate('/fleet/arrivals') }}
                className="flex h-8 items-center gap-1.5 rounded-[8px] border border-[rgba(15,23,42,0.08)]
                            bg-white px-2.5 text-[12px] font-medium text-[#475569] transition-colors
                            hover:border-[rgba(15,23,42,0.16)] hover:text-[#0F172A]
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
              >
                <PlaneLanding size={13} />
                Arrivals
                {arrivalsCount ? (
                  <span className="font-mono text-[11px] tabular-nums text-[#94A3B8]">{arrivalsCount}</span>
                ) : null}
              </button>
            </div>
          )}
          <SelectMenu
            label="Time"
            align="right"
            value={timeWindow}
            onPick={(id) => setTimeWindow(id as TimeWindow)}
            options={TIME_WINDOWS.map((w) => ({
              id: w.id,
              label: w.label,
              hint: w.hint,
            }))}
          />
        </div>
        <KpiStrip>
          <KpiCard label="Live vehicles" value={k.total} unit="on corridor" dot
                   tone={k.total ? 'ok' : 'danger'}
                   sub={k.stale ? `${k.stale} not reporting` : 'fleet presence (live)'} />
          {k.histMode ? (
            <>
              <KpiCard label="Trips" value={k.trips ?? 0} unit="in window" dot tone="neutral"
                       sub={k.km != null ? `${Math.round(k.km)} km` : 'from reporter'} />
              {isAdmin ? (
                <KpiCard
                  label={timeWindow === 'today' ? 'Alerts today' : 'Alerts'}
                  value={k.open}
                  unit="events"
                  dot
                  tone={k.critical ? 'danger' : k.open ? 'warn' : 'ok'}
                  sub={k.critical ? `${k.critical} critical` : 'none critical'}
                  onClick={() => openAlertsTab('all')}
                />
              ) : null}
              {isAdmin ? (
                <KpiCard
                  label="Critical"
                  value={k.critical}
                  unit="events"
                  dot
                  tone={k.critical ? 'danger' : 'ok'}
                  sub={k.critical ? 'needs response' : 'none critical'}
                  onClick={() => openAlertsTab('critical')}
                />
              ) : null}
              <KpiCard label="Driver events" value={k.driverAlerts} unit="behaviour" dot
                       tone={k.driverAlerts ? 'warn' : 'ok'}
                       sub="speeding + harsh + stops" />
              <KpiCard label="Actionables" value={k.actionables} unit="need attention" dot
                       tone={k.actionables ? 'danger' : 'ok'}
                       sub={`${k.breakdowns} down · ${k.critical} critical`} />
            </>
          ) : (
            <>
              <KpiCard label="Moving" value={k.moving} unit="trucks" dot tone="neutral"
                       sub={k.avgSpeed != null ? `${k.avgSpeed} KMPH avg` : 'no speed data'} />
              <KpiCard label="Idle" value={k.idle} unit="trucks" dot
                       tone={k.idle > k.total / 3 ? 'warn' : 'neutral'}
                       sub={k.charging ? `${k.charging} charging` : 'watch dwell'} />
              {isAdmin ? (
                <KpiCard label="Open alerts" value={k.open} unit="events" dot
                         tone={k.critical ? 'danger' : k.open ? 'warn' : 'ok'}
                         sub={k.critical ? `${k.critical} critical` : 'live counts'}
                         onClick={() => openAlertsTab('all')} />
              ) : null}
              {isAdmin ? (
                <KpiCard label="Critical" value={k.critical} unit="events" dot
                         tone={k.critical ? 'danger' : 'ok'}
                         sub={k.critical ? 'needs response' : 'none critical'}
                         onClick={() => openAlertsTab('critical')} />
              ) : null}
              <KpiCard label="Actionables" value={k.actionables} unit="need attention" dot
                       tone={k.actionables ? 'danger' : 'ok'}
                       sub={`${k.breakdowns} down · live`} />
              <KpiCard label="Driver events" value={k.driverAlerts} unit="behaviour" dot
                       tone={k.driverAlerts ? 'warn' : 'ok'}
                       sub="speeding + harsh handling" />
              <KpiCard label="Congested nodes" value={k.congested} unit="over threshold" dot
                       tone={k.congested ? 'warn' : 'ok'}
                       sub={k.congested ? `${k.cells} cells live` : 'all clear'} />
            </>
          )}
        </KpiStrip>
      </div>
      )}

      <main className={fullMap ? 'flex min-h-0 flex-1' : 'flex min-h-0 flex-1 gap-3 p-3'}>
        {viewMode === 'map' && !fullMap && panelCollapsed && (
        <button
          type="button"
          onClick={() => { setPanelCollapsed(false); writePanelCollapsed(false) }}
          title="Expand fleet panel"
          className="flex w-11 shrink-0 flex-col items-center gap-2 rounded-[14px] border
                      border-[rgba(15,23,42,0.07)] bg-white pt-3 text-[#64748B] shadow-[0_1px_2px_rgba(15,23,42,0.04)]
                      hover:text-[#0F172A] focus-visible:outline-none focus-visible:ring-2
                      focus-visible:ring-[#2563EB]"
        >
          <ChevronsRight size={16} />
          <span className="font-mono text-[11px] tabular-nums text-[#94A3B8]">{k.total}</span>
        </button>
        )}
        {viewMode === 'map' && !fullMap && !panelCollapsed && (
        <div className="flex w-[340px] shrink-0 flex-col">
          <SidePanel
            tabs={[
              { id: 'fleet', label: 'FLEET', count: k.total },
              // k.open, not groupedDisplayAlerts.length: the alerts LIST is fetched capped
              // at 200 rows (loadHistory's /api/alerts?limit=200) for display performance,
              // but the badge must show the real day total -- the same number the "Alerts
              // today" KPI card shows -- or the two disagree by an order of magnitude on a
              // busy day, which is exactly the bug this fixes.
              //
              // Deliberately NOT TTL'd (.claude/work/SPEC.md alert-visibility-ttl, ship
              // Phase 4 round 2): the live window's card list ages out after 5 minutes but
              // this badge keeps counting the real open total on purpose, same as the
              // "Alerts today" KPI it's pinned to above -- re-deriving it from the TTL'd
              // list would make the two disagree instead, the identical bug in the other
              // direction. The empty-state copy below was reworded so it no longer implies
              // "clear" while this badge can still be nonzero.
              ...(isAdmin ? [{ id: 'alerts', label: 'ALERTS', count: k.open }] : []),
              { id: 'trips', label: 'TRIPS', count: undefined },
              { id: 'actionables', label: 'ACTIONABLES', count: k.actionables },
              { id: 'zones', label: 'GEOFENCES', count: k.fences },
            ]}
            active={tab}
            onTab={setTab}
            headerActions={(
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setDetailFocus('browse')
                    setViewMode('detail')
                  }}
                  className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-[8px] border
                              border-[rgba(15,23,42,0.08)] bg-white text-[11px] font-medium text-[#475569]
                              transition-colors hover:border-[rgba(15,23,42,0.16)] hover:text-[#0F172A]
                              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                  title="Show fleet, alerts and zones as a full-page card layout"
                >
                  <LayoutGrid size={13} />
                  Full page details
                </button>
                <button
                  type="button"
                  onClick={() => { setPanelCollapsed(true); writePanelCollapsed(true) }}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border
                              border-[rgba(15,23,42,0.08)] bg-white text-[#475569] transition-colors
                              hover:border-[rgba(15,23,42,0.16)] hover:text-[#0F172A]
                              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                  title="Collapse panel for more map"
                >
                  <ChevronsLeft size={13} />
                </button>
              </div>
            )}
          >
            {tab === 'fleet' && (
              <div className="flex min-h-0 flex-1 flex-col gap-2">
                {selectedFence && (
                  <div className="flex items-center gap-2 rounded-[10px] border border-[#BFDBFE] bg-[#EFF6FF] px-2.5 py-1.5 text-[11px] text-[#1D4ED8]">
                    <span className="min-w-0 flex-1 truncate font-medium">
                      In{' '}
                      {geofences?.features.find((f) => f.properties.geofence_id === selectedFence)?.properties.name
                        ?? selectedFence}
                      <span className="ml-1 font-mono text-[#64748B]">({rows.length})</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => selectFence(null)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                    >
                      <X size={12} strokeWidth={2.4} />
                      Show all
                    </button>
                  </div>
                )}
                <SearchBox value={query} onChange={setQuery}
                           placeholder="Plate, driver, trip or zone…"
                           counter={`${rows.length}/${k.total}`} />
                <FilterChips
                  options={FLEET_FILTERS.map((f) => ({
                    ...f,
                    count: f.id === 'all' ? k.total
                      : f.id === 'flagged' ? worstByVehicle.size
                      // fleetRoster, not ultratechVehicles -- see its own comment. A chip
                      // count that excludes TIMS orphans while the list below it includes
                      // them is the same "No signal" mismatch k's own bucket tally fixes.
                      : fleetRoster.filter((r) => r.st === f.id).length,
                  }))}
                  active={filter}
                  onPick={setFilter}
                />
                <SortBar options={SORTS} active={sort} onPick={setSort} />
                <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                  {rows.length === 0 ? (
                    <EmptyState icon={<Truck size={26} />} title="No vehicles match"
                                body="Widen the filter, or clear the search." />
                  ) : rows.map(({ v, st, ex, where, prog, m, trip, down }) => (
                    <VehicleCard
                      key={v.vehicle_id}
                      plate={v.vehicle_id}
                      // A vehicle marked down outranks a lesser alert -- a truck flagged
                      // broken down is the thing a supervisor needs to see first, the same
                      // priority the dedicated Breakdowns tab already gives it.
                      status={down ? 'alert' : toStatus(st, ex)}
                      statusLabel={down ? down.severity : statusLabel(st)}
                      place={where?.name ?? prog?.next_hub ?? trip?.site_name ?? 'in transit'}
                      // prog was missing here -- isLaden/loadLabel checks live route
                      // direction FIRST and meta.load_state only as a fallback (meta is
                      // restart-vulnerable: it only refreshes on the next fence crossing).
                      // Without prog, this card skipped straight to that fallback, which is
                      // exactly the "shown as empty" bug on trucks mid-corridor -- most of
                      // them, on a one-way haul. Other call sites (the full fleet grid)
                      // already passed prog; this one, the map's own vehicle card, did not.
                      load={loadLabel(m, trip, prog)}
                      driver={overlayDriver(trip, m)}
                      lr={trip?.lr_number}
                      trailer={trip?.trailer}
                      event={trip?.event}
                      soc={v.soc_pct}
                      speed={v.speed_kph}
                      progressPct={progressPct(prog?.km_along, prog?.km_remaining)}
                      elapsed={ago(lastSeen(v), now)}
                      exception={down ? {
                        icon: 'power',
                        text: down.note || down.category || 'no note',
                        tone: down.severity === 'immobilised' ? 'danger' : 'warn',
                      } : ex ? {
                        icon: iconFor(ex.kind),
                        text: shorten(ex, v.lat, v.lon, geofences),
                        tone: SEV_TONE[ex.severity] ?? 'warn',
                      } : null}
                      selected={selected === v.vehicle_id}
                      onClick={() => revealVehicle(v.vehicle_id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {tab === 'trips' && (
              <div className="flex min-h-0 flex-1 flex-col gap-2">
                <ReplayTripsPanel
                  activeId={tripActiveId}
                  onPickCycle={(c) => { void loadPlaybackCycle(c.cycle_id).catch(() => undefined) }}
                />
              </div>
            )}

            {tab === 'alerts' && (
              <div className="flex min-h-0 flex-1 flex-col gap-1.5">
                {alertSeverityFilter.severities.size > 0 && (
                  <div className="flex shrink-0 flex-wrap items-center gap-2 pr-0.5">
                    <button
                      type="button"
                      onClick={() => setAlertSeverityFilter(emptyFilter())}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(220,38,38,0.3)]
                                  bg-[#FEF2F2] px-2.5 py-1 text-[11px] font-medium text-[#B91C1C] transition-colors
                                  hover:bg-[#FEE2E2]"
                    >
                      Critical only
                      <X size={11} strokeWidth={2.4} />
                    </button>
                    {alertsFilterCaveat && (
                      <span className="text-[10px] text-[#94A3B8]">{alertsFilterCaveat}</span>
                    )}
                  </div>
                )}
              <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                {filteredDisplayAlerts.length === 0 ? (
                  histLoading && timeWindow !== 'live' ? (
                    <EmptyState
                      icon={<Loader2 size={26} className="animate-spin" />}
                      title="Loading alerts…"
                      body="Fetching today's history -- this can take a few seconds."
                    />
                  ) : (
                    <EmptyState
                      title={alertSeverityFilter.severities.size > 0
                        ? 'No critical alerts in window'
                        : timeWindow === 'live' ? ALERTS_EMPTY_TITLE_LIVE : ALERTS_EMPTY_TITLE_HISTORY}
                      body={alertSeverityFilter.severities.size > 0
                        ? 'Clear the filter above to see every alert in this window.'
                        : timeWindow === 'live' ? ALERTS_EMPTY_BODY_LIVE : ALERTS_EMPTY_BODY_HISTORY}
                    />
                  )
                ) : filteredDisplayAlerts.map((a) => (
                  <VehicleCard
                    key={a.alert_id}
                    plate={a.vehicle_id ?? (a.cell ? `cell ${a.cell.slice(0, 8)}` : 'fleet')}
                    status={a.severity === 'CRITICAL' ? 'alert' : 'idle'}
                    statusLabel={a.kind.replace(/_/g, ' ').toLowerCase()}
                    place={a.driver_name || '—'}
                    speed={null}
                    elapsed={formatAlertElapsed(a, now)}
                    exception={{
                      text: rewriteStopMessage(
                        a.message,
                        vehicles.get(a.vehicle_id ?? '')?.lat ?? a.lat,
                        vehicles.get(a.vehicle_id ?? '')?.lon ?? a.lon,
                        geofences,
                      ) + (a._groupCount ? ` · ${a._groupCount} events this leg` : ''),
                      tone: SEV_TONE[a.severity] ?? 'warn',
                    }}
                    onClick={() => openAlertAndReveal(a.alert_id)}
                  />
                ))}
              </div>
              </div>
            )}

            {tab === 'actionables' && (
              <div className="flex min-h-0 flex-1 flex-col gap-2">
                <div className="flex items-center gap-2 px-0.5">
                  <button
                    type="button"
                    disabled={!selected}
                    onClick={() => selected && setBreakdownFor(selected)}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgba(244,63,94,0.28)]
                               bg-[rgba(244,63,94,0.08)] px-2.5 py-1.5 text-[11px] font-medium
                               text-[#9F1239] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Wrench size={12} />
                    Mark breakdown{selected ? ` · ${selected}` : ' · select vehicle'}
                  </button>
                </div>
                <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                  {actionables.length === 0 ? (
                    <EmptyState
                      icon={<AlertOctagon size={26} />}
                      title="Nothing waiting"
                      body="Open breakdowns and unresolved criticals appear here over SSE."
                    />
                  ) : actionables.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-[10px] border border-[rgba(15,23,42,0.07)] bg-white px-2.5 py-2"
                    >
                      <VehicleCard
                        plate={item.vehicle_id ?? (item.kind === 'alert' ? 'fleet' : '—')}
                        status={item.kind === 'breakdown' ? 'alert' : 'alert'}
                        statusLabel={item.kind === 'breakdown'
                          ? `${item.severity} · down`
                          : item.title.replace(/_/g, ' ').toLowerCase()}
                        place={rewriteStopMessage(
                          item.message || (item.eta_min != null ? `ETA ${item.eta_min} min` : '—'),
                          item.vehicle_id ? vehicles.get(item.vehicle_id)?.lat : null,
                          item.vehicle_id ? vehicles.get(item.vehicle_id)?.lon : null,
                          geofences,
                        )}
                        speed={null}
                        elapsed={ago(item.ts, now)}
                        exception={{
                          icon: item.kind === 'breakdown' ? 'power' : 'alert',
                          text: item.kind === 'breakdown'
                            ? (item.title || 'Breakdown')
                            : rewriteStopMessage(
                              item.message || item.title,
                              item.vehicle_id ? vehicles.get(item.vehicle_id)?.lat : null,
                              item.vehicle_id ? vehicles.get(item.vehicle_id)?.lon : null,
                              geofences,
                            ),
                          tone: item.severity === 'immobilised' || item.severity === 'CRITICAL'
                            ? 'danger' : 'warn',
                        }}
                        onClick={() => {
                          if (item.vehicle_id) revealVehicle(item.vehicle_id)
                          if (item.kind === 'alert' && item.alert_id) openAlertAndReveal(item.alert_id)
                        }}
                      />
                      {item.kind === 'breakdown' && item.vehicle_id && (
                        <>
                          <button
                            type="button"
                            onClick={() => setBreakdownFor(item.vehicle_id!)}
                            className="mt-1 w-full rounded-md px-2 py-1 text-left text-[11px]
                                       text-[#9F1239] hover:bg-[rgba(244,63,94,0.06)]"
                          >
                            Update / clear breakdown…
                          </button>
                          {breakdowns.get(item.vehicle_id) && (
                            <div className="mt-1 px-2 pb-1">
                              <BreakdownQuickResolve
                                vehicleId={item.vehicle_id}
                                breakdown={breakdowns.get(item.vehicle_id)!}
                                compact
                              />
                            </div>
                          )}
                        </>
                      )}
                      {item.kind === 'alert' && item.vehicle_id && (
                        <button
                          type="button"
                          onClick={() => setBreakdownFor(item.vehicle_id!)}
                          className="mt-1 w-full rounded-md px-2 py-1 text-left text-[11px]
                                     text-[#9F1239] hover:bg-[rgba(244,63,94,0.06)]"
                        >
                          Mark vehicle breakdown…
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'zones' && (
              <div className="flex min-h-0 flex-1 flex-col gap-2">
                {!geofences?.features.length ? (
                  <EmptyState title="No geofences loaded"
                              body="Run make geofences to parse the Earth project." />
                ) : (
                  <>
                    <SearchBox
                      value={query}
                      onChange={setQuery}
                      placeholder="Search place, hub or type…"
                      counter={`${zoneSummary.shown}/${k.fences}`}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowZoneTypes((v) => !v)}
                        className="flex h-8 flex-1 items-center justify-between rounded-[10px] border border-[rgba(15,23,42,0.07)] bg-white px-2.5 text-[11px] font-medium text-[#334155] transition-colors hover:border-[rgba(15,23,42,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                        aria-expanded={showZoneTypes}
                      >
                        <span>
                          {showZoneTypes ? 'Hide types' : 'Show types'}
                          <span className="ml-1.5 font-mono text-[#94A3B8]">
                            {zoneType === 'all' ? k.fences : zoneTypeChips.find((c) => c.id === zoneType)?.count ?? 0}
                          </span>
                          {zoneType !== 'all' && (
                            <span className="ml-1.5 text-[#2563EB]">· {fenceStyle(zoneType).label}</span>
                          )}
                        </span>
                        {showZoneTypes
                          ? <ChevronUp size={14} strokeWidth={2.2} className="text-[#94A3B8]" />
                          : <ChevronDown size={14} strokeWidth={2.2} className="text-[#94A3B8]" />}
                      </button>
                    </div>
                    {showZoneTypes && (
                      <FilterChips
                        options={zoneTypeChips}
                        active={zoneType}
                        onPick={setZoneType}
                      />
                    )}
                    <div className="flex items-center justify-between rounded-[10px] bg-[#F8FAFC] px-2.5 py-1.5 text-[11px] text-[#64748B]">
                      <span>
                        <span className="font-semibold text-[#0F172A]">{zoneSummary.occupied}</span>
                        {' '}occupied
                      </span>
                      <span className="font-mono tabular-nums">
                        <span className="font-semibold text-[#2563EB]">{zoneSummary.trucks}</span>
                        {' '}trucks inside
                      </span>
                    </div>
                    {selectedFence && (
                      <div className="flex items-center gap-2 rounded-[10px] border border-[#BFDBFE] bg-[#EFF6FF] px-2.5 py-1.5 text-[11px] text-[#1D4ED8]">
                        <span className="min-w-0 flex-1 truncate font-medium">
                          Showing vehicles in{' '}
                          {geofences.features.find((f) => f.properties.geofence_id === selectedFence)?.properties.name
                            ?? selectedFence}
                        </span>
                        <button
                          type="button"
                          onClick={() => selectFence(null)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                        >
                          <X size={12} strokeWidth={2.4} />
                          Show all
                        </button>
                      </div>
                    )}
                    <div className="min-h-0 flex-1 space-y-2 overflow-auto pr-0.5">
                      {zoneRows.length === 0 ? (
                        <EmptyState title="No zones match"
                                    body="Widen the type filter, or clear the search." />
                      ) : zoneRows.map(({ p, inside }) => (
                        <GeofenceCard
                          key={p.geofence_id}
                          name={p.name}
                          type={String(p.type)}
                          hubCode={p.hub_code}
                          inside={inside}
                          speedLimitKph={p.speed_limit_kph}
                          tatMinutes={p.tat_minutes}
                          areaKm2={p.area_km2}
                          selected={selectedFence === p.geofence_id}
                          onClick={() => {
                            if (selectedFence === p.geofence_id) {
                              selectFence(null)
                              return
                            }
                            selectFence(p.geofence_id)
                            flyTo({
                              lon: p.centroid_lon,
                              lat: p.centroid_lat,
                              zoom: 14,
                            })
                            setTab('fleet')
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </SidePanel>
        </div>
        )}

        <section className={`relative flex min-w-0 flex-1 flex-col overflow-hidden ${
          viewMode === 'detail' || fullMap
            ? ''
            : 'rounded-[14px] border border-[rgba(15,23,42,0.07)] bg-white'
        }`}>
          {viewMode === 'detail' ? (
            <>
              <DetailWorkspace
                tabs={[
                  { id: 'fleet', label: 'FLEET', count: k.total },
                  // ARRIVALS sits second, right after the fleet: it is what a gate crew and
                  // a loading crew look at, and it was invisible while it lived in the
                  // Reports drawer.
                  { id: 'arrivals', label: 'ARRIVALS', count: arrivalsCount },
                  // k.open, not groupedDisplayAlerts.length: the alerts LIST is fetched capped
              // at 200 rows (loadHistory's /api/alerts?limit=200) for display performance,
              // but the badge must show the real day total -- the same number the "Alerts
              // today" KPI card shows -- or the two disagree by an order of magnitude on a
              // busy day, which is exactly the bug this fixes.
              //
              // Deliberately NOT TTL'd (.claude/work/SPEC.md alert-visibility-ttl, ship
              // Phase 4 round 2): the live window's card list ages out after 5 minutes but
              // this badge keeps counting the real open total on purpose, same as the
              // "Alerts today" KPI it's pinned to above -- re-deriving it from the TTL'd
              // list would make the two disagree instead, the identical bug in the other
              // direction. The empty-state copy below was reworded so it no longer implies
              // "clear" while this badge can still be nonzero.
              ...(isAdmin ? [{ id: 'alerts', label: 'ALERTS', count: k.open }] : []),
                  { id: 'trips', label: 'TRIPS', count: undefined },
                  { id: 'actionables', label: 'ACTIONABLES', count: k.actionables },
                  { id: 'breakdowns', label: 'BREAKDOWNS', count: k.breakdowns },
                  { id: 'zones', label: 'GEOFENCES', count: k.fences },
                ]}
                activeTab={tab}
                onTab={setTab}
                onBackToMap={() => setViewMode('map')}
                onReplayBreakdown={(id) => { void loadBreakdownReplay(id).catch(() => undefined) }}
                vehicleFocusId={detailFocus === 'vehicle' ? selected : null}
                onClearVehicleFocus={() => setDetailFocus('browse')}
                fleetRows={rows.map(({ v, st, ex, where, prog, m, trip, down }) => ({
                  vehicleId: v.vehicle_id,
                  status: down ? 'alert' : toStatus(st, ex),
                  statusLabel: down ? down.severity : statusLabel(st),
                  place: where?.name ?? prog?.next_hub ?? 'in transit',
                  load: loadLabel(m, trip, prog),
                  driver: overlayDriver(trip, m),
                  lr: trip?.lr_number,
                  trailer: trip?.trailer,
                  event: trip?.event,
                  soc: v.soc_pct,
                  speed: v.speed_kph,
                  progressPct: progressPct(prog?.km_along, prog?.km_remaining),
                  elapsed: ago(lastSeen(v), now),
                  exception: down ? {
                    icon: 'power' as const,
                    text: down.note || down.category || 'no note',
                    tone: down.severity === 'immobilised' ? 'danger' as const : 'warn' as const,
                  } : ex ? {
                    icon: iconFor(ex.kind),
                    text: shorten(ex, v.lat, v.lon, geofences),
                    tone: SEV_TONE[ex.severity] ?? 'warn',
                  } : null,
                }))}
                fleetTotal={k.total}
                fleetQuery={query}
                onFleetQuery={setQuery}
                fleetFilter={filter}
                onFleetFilter={setFilter}
                fleetFilterOptions={FLEET_FILTERS.map((f) => ({
                  ...f,
                  count: f.id === 'all' ? k.total
                    : f.id === 'flagged' ? worstByVehicle.size
                    : [...ultratechVehicles.values()].filter((v) => vehicleState(v, now) === f.id).length,
                }))}
                fleetSort={sort}
                onFleetSort={setSort}
                fleetSortOptions={SORTS}
                selectedVehicle={selected}
                selectedFence={selectedFence}
                selectedFenceName={
                  selectedFence
                    ? geofences?.features.find((f) => f.properties.geofence_id === selectedFence)?.properties.name
                      ?? selectedFence
                    : null
                }
                onClearFence={() => selectFence(null)}
                onSelectVehicle={(id) => {
                  selectVehicle(id)
                  setDetailFocus('vehicle')
                }}
                alerts={filteredDisplayAlerts}
                alertsEmptyTitle={alertSeverityFilter.severities.size > 0
                  ? 'No critical alerts in window'
                  : timeWindow === 'live' ? ALERTS_EMPTY_TITLE_LIVE : ALERTS_EMPTY_TITLE_HISTORY}
                alertsEmptyBody={alertSeverityFilter.severities.size > 0
                  ? 'Clear the filter above to see every alert in this window.'
                  : timeWindow === 'live' ? ALERTS_EMPTY_BODY_LIVE : ALERTS_EMPTY_BODY_HISTORY}
                alertsFilterActive={alertSeverityFilter.severities.size > 0}
                onClearAlertsFilter={() => setAlertSeverityFilter(emptyFilter())}
                alertsFilterCaveat={alertsFilterCaveat}
                alertElapsed={(a) => formatAlertElapsed(a, now)}
                onOpenAlert={openAlertAndReveal}
                alertTone={(sev) => SEV_TONE[sev] ?? 'warn'}
                actionables={actionables}
                actionableElapsed={(ts) => ago(ts, now)}
                onActionableClick={(item) => {
                  if (item.vehicle_id) {
                    selectVehicle(item.vehicle_id)
                    setDetailFocus('vehicle')
                  }
                  if (item.kind === 'alert' && item.alert_id) openAlertAndReveal(item.alert_id)
                }}
                onMarkBreakdown={setBreakdownFor}
                onLocateVehicle={revealVehicleOnMap}
                canMarkSelected={Boolean(selected)}
                selectedPlate={selected}
                zonesLoaded={Boolean(geofences?.features.length)}
                zoneRows={zoneRows.map(({ p, inside }) => ({
                  id: p.geofence_id,
                  name: p.name,
                  type: String(p.type),
                  hubCode: p.hub_code,
                  inside,
                  speedLimitKph: p.speed_limit_kph,
                  tatMinutes: p.tat_minutes,
                  areaKm2: p.area_km2,
                }))}
                zoneQuery={query}
                onZoneQuery={setQuery}
                zoneCounter={`${zoneSummary.shown}/${k.fences}`}
                showZoneTypes={showZoneTypes}
                onToggleZoneTypes={() => setShowZoneTypes((v) => !v)}
                zoneType={zoneType}
                zoneTypeLabel={zoneType !== 'all' ? fenceStyle(zoneType).label : null}
                zoneTypeChips={zoneTypeChips}
                onZoneType={setZoneType}
                zoneSummary={{ occupied: zoneSummary.occupied, trucks: zoneSummary.trucks }}
                fencesTotal={k.fences}
                onSelectZone={(id) => {
                  if (selectedFence === id) {
                    selectFence(null)
                    return
                  }
                  selectFence(id)
                  flyTo({
                    lon: zoneRows.find((r) => r.p.geofence_id === id)?.p.centroid_lon ?? 0,
                    lat: zoneRows.find((r) => r.p.geofence_id === id)?.p.centroid_lat ?? 0,
                    zoom: 14,
                  })
                  setTab('fleet')
                }}
                tripActiveId={tripActiveId}
                onPickCycle={(c) => {
                  // From Full page details: show the map the replay plays on, like a breakdown
                  // replay does -- otherwise only the scrubber bar appears and the map stays hidden.
                  void loadPlaybackCycle(c.cycle_id).then(() => setViewMode('map')).catch(() => undefined)
                }}
                onReplayVehicleTrip={(id) => {
                  void replayVehicleTrip(id).catch(() => undefined)
                }}
                tripReplaySlot={tripReplayBar}
              />
              {openAlert && <AlertDetail alertId={openAlert} fallback={histFallback} />}
              {breakdownFor && (
                <BreakdownForm
                  vehicleId={breakdownFor}
                  phone={meta.get(breakdownFor)?.driver_phone}
                  onClose={() => setBreakdownFor(null)}
                />
              )}
            </>
          ) : (
            <>
          {/* Map and replay bar on the left; during a replay the trip timeline is its own
              full-height column on the right, so the bar only spans the map. */}
          <div className="flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 flex-col">
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <MapCanvas
              theme="light"
              liveOnly={fullMap}
              // While a replay is open the map shows ONLY the replayed truck: an empty frame
              // (no position yet) draws no vehicle, never the whole live fleet.
              replayFrame={fullMap ? null : tripReplay ? (tripFrame?.vehicles ?? []) : null}
              replayMeta={fullMap ? null : (tripFrame?.meta ?? null)}
              replayAlerts={fullMap ? null : tripReplayAlertMarkers}
              cameraFollow={!fullMap && Boolean(tripReplay && tripFollow)}
              onUserCamera={() => setTripFollow(false)}
            />

            {!fullMap && (
            <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center p-3">
              <div className="pointer-events-auto flex flex-col items-center gap-2">
                <MapModeSwitch modes={MAP_MODES} active={mode} onPick={setMode} />
                {selectedFence && (
                  <div className="flex items-center gap-2 rounded-full border border-[#BFDBFE] bg-white/95 px-3 py-1.5 text-[12px] text-[#1D4ED8] shadow-[0_2px_8px_rgba(15,23,42,0.08)] backdrop-blur-sm">
                    <span className="max-w-[220px] truncate font-medium">
                      {geofences?.features.find((f) => f.properties.geofence_id === selectedFence)?.properties.name
                        ?? 'Geofence'}
                    </span>
                    <button
                      type="button"
                      onClick={() => selectFence(null)}
                      className="inline-flex items-center gap-1 rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[11px] font-semibold hover:bg-[#DBEAFE] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                    >
                      <X size={12} strokeWidth={2.4} />
                      Show all
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}

            {tripReplay && !fullMap && !timelineOpen && (
              <button
                type="button"
                onClick={() => setTimelineOpen(true)}
                className="absolute right-3 top-[84px] z-20 hidden h-9 items-center gap-1.5 rounded-full border border-[rgba(15,23,42,0.08)] bg-white px-3 text-[12px] font-semibold text-[#0F172A] shadow-[0_4px_14px_rgba(15,23,42,0.12)] hover:text-[#1D4ED8] lg:inline-flex"
              >
                <Clock size={13} className="text-[#2563EB]" /> Trip timeline
              </button>
            )}

            {!fullMap && (
            <div className="absolute bottom-3 left-3 z-10">
              <MapLegendCard
                title={MAP_MODES.find((m) => m.id === mode)?.title ?? 'Map legend'}
                rows={legendRows}
                footer={tripReplay
                  ? (tripPath?.length
                    ? `Trip replay · ${tripPath.length} GPS fixes + geotags/halts`
                    : 'Trip replay from geotags + halts')
                  : 'Counts are live from telemetry'}
              />
            </div>
            )}

            {/* Bottom-right column: corridor pressure, then the basemap chip beneath it.
                Right-aligned so the switcher's expanded list grows leftward into the map
                instead of pushing the column off the edge. */}
            <div className="absolute bottom-3 right-3 z-10 flex flex-col items-end gap-2">
              {/* SHOWN IN FULL MAP TOO. It was hidden there along with the rest of the
                  chrome, but it is not chrome -- it is a map overlay, and full screen is
                  where somebody is actually watching the corridor. Hiding it there removed
                  it from the one view it exists for, which reads as the panel having
                  broken rather than having been put away. */}
              {/* Hidden while a trip replay plays: it describes the LIVE corridor, not the
                  replayed moment, and sits over the replayed truck's path. */}
              {!tripReplay && (
                <CorridorPressure
                  title="Corridor pressure"
                  verdict={pressure.verdict}
                  verdictTone={pressure.verdictTone}
                  bars={pressure.bars}
                  axis={['DHAR', 'JHULWANIA', 'DHULE']}
                />
              )}
              <BasemapSwitch />
            </div>

            {!fullMap && selected && (
              <VehicleDrawer
                vehicleId={selected}
                onOpenFullPage={() => {
                  setDetailFocus('vehicle')
                  setViewMode('detail')
                }}
                onReplayTrip={replaySelectedVehicleTrip}
              />
            )}
            {!fullMap && openAlert && <AlertDetail alertId={openAlert} fallback={histFallback} />}
            {!fullMap && breakdownFor && (
              <BreakdownForm
                vehicleId={breakdownFor}
                phone={meta.get(breakdownFor)?.driver_phone}
                onClose={() => setBreakdownFor(null)}
              />
            )}
          </div>
          {!fullMap && tripReplay && tripReplayBar}
          </div>
          {tripReplay && !fullMap && timelineOpen && (
            <div className="hidden w-[360px] shrink-0 border-l border-[rgba(15,23,42,0.08)] lg:flex">
              <TripTimelinePanel
                variant="column"
                openLr={openLr}
                onOpenLr={setOpenLr}
                onCloseLr={() => setOpenLr(null)}
                liveTims={tripReplay.playback?.status === 'live' ? timsTrips.get(tripReplay.vehicle_id) : undefined}
                payload={tripReplay}
                alerts={tripReplayAlertMarkers ?? []}
                at={tripAt}
                playing={tripPlaying}
                onSeek={(ts) => { setTripAt(ts); setTripPlaying(false) }}
                onClose={() => setTimelineOpen(false)}
              />
            </div>
          )}
          </div>
            </>
          )}
        </section>
      </main>
    </div>
  )
}

/** WHEN IT FIRED, not only how long ago. "18 min ago" is the right thing to read at a
 *  glance and the wrong thing to write in a call log or match against a driver's account
 *  of his shift -- both need a clock time. One function, used by both the Live Ops side
 *  panel and the Detail Workspace's alerts tab, so the two formats cannot drift apart the
 *  way they already did once (the Detail Workspace copy shipped without the clock-time
 *  prefix). */
export function formatAlertElapsed(a: Pick<Alert, 'ts' | '_groupCount' | '_groupFirstTs'>, now: number): string {
  return a._groupCount
    ? `${clock(a._groupFirstTs ?? a.ts)}–${clock(a.ts)} · ${a._groupCount}×`
    : `${clock(a.ts)} · ${ago(a.ts, now)}`
}

function toStatus(st: string, ex?: Alert): VehicleStatus {
  if (ex && ex.severity === 'CRITICAL') return 'alert'
  if (st === 'moving') return 'moving'
  if (st === 'charging') return 'loading'
  if (st === 'stale') return 'offline'
  return 'idle'
}

function statusLabel(st: string): string {
  return st === 'stale' ? 'Offline' : st.charAt(0).toUpperCase() + st.slice(1)
}

/** Fraction of the leg completed, or null when the topology has not placed it yet. */
function progressPct(along?: number | null, remaining?: number | null): number | null {
  if (along == null || remaining == null) return null
  const total = along + remaining
  return total > 0 ? Math.round((along / total) * 100) : null
}

function iconFor(kind: string): 'clock' | 'zap' | 'power' | 'alert' {
  if (kind.includes('OVERSPEED') || kind.includes('HARSH') || kind === 'RASH_DRIVING') return 'zap'
  if (kind.includes('STOP') || kind.includes('TAT') || kind.includes('DWELL')) return 'clock'
  if (kind.includes('SOC') || kind.includes('STALE')) return 'power'
  return 'alert'
}

/** Chips are one line on a 340px column; the full text lives in the alert drawer. */
function shorten(
  a: Alert,
  lat?: number | null,
  lon?: number | null,
  geofences?: Parameters<typeof rewriteStopMessage>[3],
): string {
  const m = rewriteStopMessage(a.message, lat ?? a.lat, lon ?? a.lon, geofences)
  return m.length > 58 ? `${m.slice(0, 56)}…` : m
}

export { duration }
